// src/types/simulation.ts
var DEFAULT_SIMULATION_OPTIONS = {
  maxIterations: 1e3,
  tolerance: 1e-3,
  timeStep: 1e-3
};

// src/types/user.ts
var DEFAULT_USER_SETTINGS = {
  theme: "auto",
  soundEnabled: true,
  animationsEnabled: true,
  gridSnap: true,
  showHints: true
};

// src/types/curriculum.ts
var GRADE_LEVEL_INFO = {
  "K-2": {
    level: "K-2",
    ageRange: "5-7 years",
    ukEquivalent: "KS1",
    description: "Introduction to basic concepts through play and exploration",
    recommendedPaths: ["circuits-explorers"],
    keySkills: ["Basic circuit concepts", "On/Off states", "Simple connections"]
  },
  "3-5": {
    level: "3-5",
    ageRange: "8-10 years",
    ukEquivalent: "KS2",
    description: "Building foundational knowledge of electricity and circuits",
    recommendedPaths: ["circuits-explorers", "electricity-basics"],
    keySkills: ["Complete circuits", "Series connections", "Conductors & insulators"]
  },
  "6-8": {
    level: "6-8",
    ageRange: "11-13 years",
    ukEquivalent: "KS3",
    description: "Applying scientific principles to circuit design",
    recommendedPaths: ["electricity-basics", "ohms-law-mastery", "digital-logic-intro"],
    keySkills: ["Ohm's Law", "Series & Parallel circuits", "Current & Voltage"]
  },
  "9-12": {
    level: "9-12",
    ageRange: "14-18 years",
    ukEquivalent: "KS4",
    description: "Advanced circuit analysis and engineering principles",
    recommendedPaths: ["ohms-law-mastery", "digital-logic-intro", "electronics-engineering"],
    keySkills: ["Circuit analysis", "Transistors", "Logic gates", "Signal processing"]
  }
};

// src/utils/helpers.ts
function generateId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
function distance(x1, y1, x2, y2) {
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}
function snapToGrid(value, gridSize) {
  return Math.round(value / gridSize) * gridSize;
}
function degToRad(degrees) {
  return degrees * Math.PI / 180;
}
function radToDeg(radians) {
  return radians * 180 / Math.PI;
}
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
function formatSI(value, unit, decimals = 2) {
  const prefixes = [
    { threshold: 1e12, prefix: "T" },
    { threshold: 1e9, prefix: "G" },
    { threshold: 1e6, prefix: "M" },
    { threshold: 1e3, prefix: "k" },
    { threshold: 1, prefix: "" },
    { threshold: 1e-3, prefix: "m" },
    { threshold: 1e-6, prefix: "\u03BC" },
    { threshold: 1e-9, prefix: "n" },
    { threshold: 1e-12, prefix: "p" }
  ];
  const absValue = Math.abs(value);
  for (const { threshold, prefix } of prefixes) {
    if (absValue >= threshold) {
      return `${(value / threshold).toFixed(decimals)} ${prefix}${unit}`;
    }
  }
  return `${value.toFixed(decimals)} ${unit}`;
}
function debounce(fn, delay) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
function throttle(fn, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

// src/utils/constants.ts
var GRID_SIZE = 20;
var CANVAS_DEFAULT_WIDTH = 800;
var CANVAS_DEFAULT_HEIGHT = 600;
var COMPONENT_DEFAULTS = {
  battery: {
    voltage: 9,
    width: 60,
    height: 40
  },
  resistor: {
    resistance: 1e3,
    width: 60,
    height: 20
  },
  led: {
    forwardVoltage: 2,
    maxCurrent: 0.02,
    width: 30,
    height: 40
  },
  switch: {
    isOpen: true,
    width: 40,
    height: 30
  },
  wire: {
    width: 0,
    height: 0
  },
  and_gate: {
    width: 60,
    height: 50
  },
  or_gate: {
    width: 60,
    height: 50
  },
  not_gate: {
    width: 50,
    height: 40
  },
  ground: {
    width: 30,
    height: 30
  },
  capacitor: {
    capacitance: 1e-6,
    // 1μF
    width: 40,
    height: 30
  },
  diode: {
    forwardVoltage: 0.7,
    reverseVoltage: 50,
    width: 50,
    height: 25
  },
  transistor: {
    gain: 100,
    // hFE
    width: 50,
    height: 50
  },
  buzzer: {
    frequency: 2e3,
    // Hz
    forwardVoltage: 3,
    width: 40,
    height: 40
  },
  motor: {
    ratedVoltage: 6,
    ratedCurrent: 0.5,
    width: 50,
    height: 50
  },
  potentiometer: {
    maxResistance: 1e4,
    position: 50,
    // 50%
    width: 50,
    height: 40
  },
  fuse: {
    rating: 1,
    // 1A
    isBlown: false,
    width: 40,
    height: 20
  }
};
var SIMULATION = {
  MAX_CURRENT: 10,
  MIN_RESISTANCE: 1e-3,
  LOGIC_HIGH_VOLTAGE: 5,
  LOGIC_LOW_VOLTAGE: 0,
  LOGIC_THRESHOLD_VOLTAGE: 2.5,
  OVERLOAD_FACTOR: 1.5
};
var COLORS = {
  wire: {
    default: "#374151",
    carrying: "#3b82f6",
    overloaded: "#ef4444"
  },
  led: {
    off: "#6b7280",
    red: "#ef4444",
    green: "#22c55e",
    blue: "#3b82f6",
    yellow: "#eab308"
  },
  component: {
    default: "#f3f4f6",
    selected: "#dbeafe",
    overloaded: "#fee2e2"
  },
  terminal: {
    positive: "#ef4444",
    negative: "#1f2937",
    input: "#3b82f6",
    output: "#22c55e"
  }
};
var RATING_THRESHOLDS = {
  gold: 0.5,
  silver: 0.75,
  bronze: 1
};
var ACHIEVEMENT_REQUIREMENTS = {
  first_circuit: { circuits: 1 },
  first_challenge: { challengesSolved: 1 },
  ten_challenges: { challengesSolved: 10 },
  hundred_challenges: { challengesSolved: 100 },
  parallel_master: { parallelCircuits: 5 },
  logic_master: { logicChallenges: 10 },
  efficiency_expert: { goldRatings: 10 },
  speed_demon: { speedrunBest: true },
  creator: { circuitsCreated: 1 },
  popular_creator: { totalLikes: 100 },
  streak_7: { streakDays: 7 },
  streak_30: { streakDays: 30 }
};

// src/data/componentEducation.ts
var COMPONENT_EDUCATION = {
  battery: {
    name: "Battery",
    symbol: "+||-",
    description: "A battery gives power to your circuit! It's like the engine that makes everything work.",
    howItWorks: "Inside a battery, special chemicals create electricity. The electricity flows out from one side (positive +) and comes back to the other side (negative -).",
    formula: "P = V \xD7 I",
    formulaDescription: "Power = Voltage \xD7 Current (how much energy you get)",
    realWorldExamples: [
      "TV remote controls (AA batteries)",
      "Toy cars and robots",
      "Flashlights",
      "Game controllers"
    ],
    safetyTips: [
      "Never connect + and - together without anything in between",
      "Keep batteries in a cool, dry place",
      "Recycle old batteries - don't throw them in the trash"
    ],
    funFact: "The first battery was invented in 1800 - over 200 years ago!",
    category: "power"
  },
  resistor: {
    name: "Resistor",
    symbol: "////",
    description: "A resistor slows down electricity, like a speed bump for electrons! It protects other parts from too much power.",
    howItWorks: "Resistors are made of special materials that make it harder for electricity to flow through. This slows down the electricity and keeps it at a safe level.",
    formula: "V = I \xD7 R",
    formulaDescription: "Voltage = Current \xD7 Resistance (Ohm's Law - a super important rule!)",
    realWorldExamples: [
      "Volume knobs on speakers",
      "Protecting LEDs from burning out",
      "Dimmer switches for lights",
      "Game controllers"
    ],
    safetyTips: [
      "Use the right resistor or parts might get too hot",
      "Bigger numbers = more slowing down of electricity"
    ],
    funFact: "Resistors have colored stripes that are like a secret code telling you how strong they are!",
    category: "passive"
  },
  led: {
    name: "LED (Light)",
    symbol: "\u25B7|",
    description: "An LED is a tiny light that glows when electricity flows through it. Different LEDs make different colors!",
    howItWorks: "When electricity goes through the LED, it lights up! LEDs only work one way, so you need to connect them the right way around. The long leg is positive (+) and the short leg is negative (-).",
    formula: "R = (Vs - Vf) / I",
    formulaDescription: "How to find the right resistor for your LED",
    realWorldExamples: [
      "Power lights on computers and TVs",
      "Traffic lights",
      "Christmas lights",
      "Flashlights",
      "Your phone screen"
    ],
    safetyTips: [
      "Always use a resistor with your LED or it will burn out!",
      "Connect it the right way - long leg to positive (+)",
      "Don't stare at super bright LEDs"
    ],
    funFact: "LEDs use way less energy than old light bulbs and can last for over 10 years!",
    category: "output"
  },
  switch: {
    name: "Switch",
    symbol: "/ o",
    description: "A switch lets you turn your circuit ON or OFF! It's like a gate that opens and closes for electricity.",
    howItWorks: "When you flip the switch to ON, it makes a bridge for electricity to flow across. When you flip it to OFF, it breaks the bridge and stops the electricity.",
    realWorldExamples: [
      "Light switches on your wall",
      "Power buttons on toys and games",
      "Keys on a keyboard",
      "The button that turns on a flashlight"
    ],
    funFact: "You probably flip light switches about 70,000 times in your whole life!",
    category: "passive"
  },
  capacitor: {
    name: "Capacitor",
    symbol: "||",
    description: "A capacitor stores electrical energy in an electric field. It can charge up and release energy quickly.",
    howItWorks: "Two conductive plates separated by an insulator (dielectric) store charge. Electrons accumulate on one plate and are depleted from the other.",
    formula: "Q = C \xD7 V",
    formulaDescription: "Charge (Coulombs) = Capacitance (Farads) \xD7 Voltage (Volts)",
    realWorldExamples: [
      "Camera flash units",
      "Power supply smoothing",
      "Touch screens",
      "Radio tuning circuits",
      "Energy storage in hybrid cars"
    ],
    safetyTips: [
      "Large capacitors can hold dangerous charge",
      "Discharge capacitors before handling",
      "Check voltage ratings"
    ],
    funFact: "Supercapacitors can charge in seconds and are being used in some buses!",
    category: "passive"
  },
  diode: {
    name: "Diode",
    symbol: "\u25B7|",
    description: "A diode is a one-way valve for electricity. It allows current to flow in only one direction.",
    howItWorks: "The P-N junction in a diode allows electrons to flow from cathode to anode but blocks reverse flow (until breakdown voltage).",
    formula: "Vf \u2248 0.7V (silicon)",
    formulaDescription: "Forward voltage drop is typically 0.7V for silicon diodes, 0.3V for germanium",
    realWorldExamples: [
      "Power supply rectifiers",
      "Reverse polarity protection",
      "Signal detection in radios",
      "Solar panel blocking diodes"
    ],
    safetyTips: [
      "Check polarity - cathode is marked with a band",
      "Don't exceed reverse voltage rating"
    ],
    funFact: `The first diodes were called "cat's whisker" detectors and used in early radios!`,
    category: "active"
  },
  transistor: {
    name: "Transistor (NPN)",
    symbol: "NPN",
    description: "A transistor is an electronic switch that can also amplify signals. It's the building block of all modern electronics!",
    howItWorks: "A small current at the base controls a larger current from collector to emitter. It's like a water faucet controlled by a small handle.",
    formula: "Ic = \u03B2 \xD7 Ib",
    formulaDescription: "Collector Current = Gain (\u03B2) \xD7 Base Current",
    realWorldExamples: [
      "Computer processors (billions of transistors!)",
      "Audio amplifiers",
      "Motor controllers",
      "Touch-sensitive switches"
    ],
    safetyTips: [
      "Don't exceed maximum voltage/current ratings",
      "Use heat sinks for power transistors"
    ],
    funFact: "A modern computer chip has more transistors than there are stars in the Milky Way!",
    category: "active"
  },
  buzzer: {
    name: "Buzzer",
    symbol: "))))",
    description: "A buzzer converts electrical signals into sound. It's used to create audio alerts and tones.",
    howItWorks: "An electromagnet vibrates a metal disc at a specific frequency, creating sound waves that we can hear.",
    realWorldExamples: [
      "Alarm clocks",
      "Microwave ovens",
      "Doorbells",
      "Game show buzzers",
      "Smoke detectors"
    ],
    safetyTips: [
      "Check voltage rating before connecting",
      "Prolonged loud buzzing can damage hearing"
    ],
    funFact: "Piezoelectric buzzers can create frequencies from 1Hz to over 100kHz!",
    category: "output"
  },
  motor: {
    name: "DC Motor",
    symbol: "M",
    description: "A motor converts electrical energy into mechanical rotation. It's how robots move!",
    howItWorks: "Current through wire coils creates magnetic fields that interact with permanent magnets, causing the rotor to spin.",
    formula: "T = k \xD7 I",
    formulaDescription: "Torque is proportional to current - more current means more force!",
    realWorldExamples: [
      "Electric fans",
      "Toy cars",
      "Power tools",
      "Electric vehicles",
      "Hard drives"
    ],
    safetyTips: [
      "Keep fingers away from rotating parts",
      "Don't stall motors - they can overheat",
      "Use flyback diodes to protect circuits"
    ],
    funFact: "The world's smallest motor is just 200 nanometers - 500 times smaller than a human hair!",
    category: "output"
  },
  potentiometer: {
    name: "Potentiometer",
    symbol: "---/\\---",
    description: "A potentiometer is a variable resistor. You can adjust its resistance by turning a knob or sliding a lever.",
    howItWorks: "A wiper moves along a resistive element, changing how much resistance is in the circuit. It's like a dimmer switch!",
    formula: "R = (position%) \xD7 Rmax",
    formulaDescription: "Effective resistance depends on wiper position",
    realWorldExamples: [
      "Volume controls on speakers",
      "Dimmer switches for lights",
      "Joystick controllers",
      "Servo position feedback"
    ],
    funFact: "Potentiometers were used in early analog computers to set variables!",
    category: "passive"
  },
  fuse: {
    name: "Fuse",
    symbol: "\u2014|\u2014",
    description: "A fuse is a safety device that protects circuits by melting and breaking the circuit if too much current flows.",
    howItWorks: "A thin wire inside the fuse heats up with current. If current exceeds the rating, the wire melts and opens the circuit.",
    formula: "I\xB2t rating",
    formulaDescription: "Fuses are rated by how much energy they can handle before blowing",
    realWorldExamples: [
      "Car fuse boxes",
      "Home electrical panels",
      "Power supplies",
      "Christmas lights"
    ],
    safetyTips: [
      "Always replace with same rating",
      "Never bypass a fuse with wire",
      "Find and fix the problem that caused the fuse to blow"
    ],
    funFact: "The first electrical fuses were invented in 1864, before light bulbs existed!",
    category: "protection"
  },
  ground: {
    name: "Ground",
    symbol: "\u23DA",
    description: "Ground is where electricity comes back to after doing its job. Think of it as the finish line in a race!",
    howItWorks: "Electricity flows from the battery's positive (+) side, through your circuit, and returns to ground. Ground is like home base - it's always at 0 volts.",
    realWorldExamples: [
      "The bottom wire in your house electricity",
      "The metal body of your car",
      "The ground pin (round one) on wall outlets"
    ],
    safetyTips: [
      "Always connect ground to complete your circuit",
      "Ground keeps electricity safe"
    ],
    funFact: "Some power lines actually use the Earth itself as a wire!",
    category: "power"
  },
  wire: {
    name: "Wire",
    symbol: "\u2014\u2014\u2014",
    description: "Wires carry electrical current from one component to another with minimal resistance.",
    howItWorks: "Free electrons in the metal conductor flow easily when voltage is applied, carrying current through the circuit.",
    realWorldExamples: [
      "Household electrical wiring",
      "USB cables",
      "Circuit board traces",
      "Power lines"
    ],
    safetyTips: [
      "Use appropriate wire gauge for current",
      "Check insulation for damage"
    ],
    funFact: "Copper wire can carry electricity at about 95% the speed of light!",
    category: "passive"
  },
  and_gate: {
    name: "AND Gate",
    symbol: "AND",
    description: `An AND gate outputs HIGH only when ALL inputs are HIGH. It's like saying "both A AND B must be true".`,
    howItWorks: "The gate compares its inputs and only produces a 1 output when all inputs are 1.",
    formula: "Y = A \xB7 B",
    formulaDescription: "Output = Input A AND Input B (both must be 1)",
    realWorldExamples: [
      "Security systems (all sensors must trigger)",
      "Seatbelt warning (seat occupied AND belt unbuckled)",
      "Safety interlocks"
    ],
    funFact: "Claude Shannon proved in 1937 that any logical operation can be built from combinations of basic gates!",
    category: "logic"
  },
  or_gate: {
    name: "OR Gate",
    symbol: "OR",
    description: `An OR gate outputs HIGH when ANY input is HIGH. It's like saying "either A OR B (or both)".`,
    howItWorks: "The gate checks if at least one input is 1, and if so, outputs 1.",
    formula: "Y = A + B",
    formulaDescription: "Output = Input A OR Input B (either or both)",
    realWorldExamples: [
      "Doorbell buttons (front OR back door)",
      "Alarm triggers (any sensor)",
      "Multi-button game controllers"
    ],
    funFact: "OR gates are used in adder circuits - the building blocks of calculators!",
    category: "logic"
  },
  not_gate: {
    name: "NOT Gate (Inverter)",
    symbol: "NOT",
    description: "A NOT gate inverts its input. HIGH becomes LOW, and LOW becomes HIGH.",
    howItWorks: "The gate flips the input signal - if input is 1, output is 0, and vice versa.",
    formula: "Y = \u0100",
    formulaDescription: "Output = NOT Input (opposite of input)",
    realWorldExamples: [
      "Indicator lights (ON when system OFF)",
      "Normally-closed switches",
      "Digital signal inversion"
    ],
    funFact: "A NOT gate is the simplest logic gate, needing just one transistor to build!",
    category: "logic"
  }
};
var COMPONENT_CATEGORIES = {
  power: {
    name: "Power",
    description: "Parts that give energy",
    components: ["battery", "ground"]
  },
  passive: {
    name: "Basic Parts",
    description: "Simple parts that control electricity",
    components: ["resistor", "capacitor", "potentiometer", "switch", "wire"]
  },
  active: {
    name: "Smart Parts",
    description: "Advanced parts that control the flow",
    components: ["diode", "transistor"]
  },
  logic: {
    name: "Brain Parts",
    description: "Parts that make decisions (ON or OFF)",
    components: ["and_gate", "or_gate", "not_gate"]
  },
  output: {
    name: "Action Parts",
    description: "Parts that make lights, sounds, or movement",
    components: ["led", "buzzer", "motor"]
  },
  protection: {
    name: "Safety Parts",
    description: "Parts that keep your circuit safe",
    components: ["fuse"]
  }
};

// src/data/tutorialData.ts
var basicsLevels = [
  {
    id: "basics_1",
    chapterId: "basics",
    order: 1,
    title: "Your First Circuit",
    description: "Learn what makes a complete circuit by connecting a battery to an LED.",
    educationalConcept: {
      title: "What is a Circuit?",
      explanation: "A circuit is a closed loop that allows electricity to flow from a power source (like a battery), through components, and back to the source. For current to flow, the circuit must be complete - any break stops the flow!",
      keyTerms: [
        { term: "Circuit", definition: "A closed path through which electric current can flow" },
        { term: "Current", definition: "The flow of electrons through a conductor, measured in Amperes (A)" },
        { term: "Complete Circuit", definition: "A circuit with no breaks, allowing current to flow continuously" }
      ],
      relatedComponents: ["battery", "led", "resistor"]
    },
    objectives: [
      {
        id: "basics_1_add_battery",
        description: "Add a battery to the canvas",
        type: "add_component",
        config: { type: "add_component", componentType: "battery" },
        required: true,
        order: 1
      },
      {
        id: "basics_1_add_resistor",
        description: "Add a resistor to protect the LED",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor" },
        required: true,
        order: 2
      },
      {
        id: "basics_1_add_led",
        description: "Add an LED to the circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "led" },
        required: true,
        order: 3
      },
      {
        id: "basics_1_connect",
        description: "Connect all components to complete the circuit",
        type: "wire_count",
        config: { type: "wire_count", min: 3 },
        required: true,
        order: 4
      },
      {
        id: "basics_1_simulate",
        description: "Run the simulation to light up the LED!",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 5
      }
    ],
    hints: [
      {
        id: "basics_1_hint_1",
        order: 1,
        content: 'Start by dragging a Battery from the "Power" section in the component palette onto the canvas.',
        triggerCondition: { type: "user_request" },
        targetElement: ".component-palette"
      },
      {
        id: "basics_1_hint_2",
        order: 2,
        content: 'LEDs need a resistor to limit current, or they will burn out! Add a resistor from the "Passive" section.',
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_1_hint_3",
        order: 3,
        content: "Click on a terminal (small dot) to start a wire, then click another terminal to connect them. Connect: Battery(+) \u2192 Resistor \u2192 LED(+) \u2192 LED(-) \u2192 Battery(-)",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 50,
    bonusXp: 25,
    timeBonusThresholdSeconds: 120,
    unlocksLevelIds: ["basics_2"]
  },
  {
    id: "basics_2",
    chapterId: "basics",
    order: 2,
    title: "The Switch",
    description: "Add control to your circuit with a switch that turns the LED on and off.",
    educationalConcept: {
      title: "Controlling Current Flow",
      explanation: "A switch is like a gate for electricity. When closed (ON), current flows through. When open (OFF), it breaks the circuit and stops current flow. This is how light switches in your home work!",
      keyTerms: [
        { term: "Switch", definition: "A device that opens or closes a circuit, controlling current flow" },
        { term: "Open Circuit", definition: "A circuit with a break - no current can flow" },
        { term: "Closed Circuit", definition: "A complete circuit - current flows freely" }
      ],
      relatedComponents: ["switch", "battery", "led"]
    },
    prerequisiteLevelId: "basics_1",
    objectives: [
      {
        id: "basics_2_add_switch",
        description: "Add a switch to control the circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "switch" },
        required: true,
        order: 1
      },
      {
        id: "basics_2_build_circuit",
        description: "Build a circuit with battery, switch, resistor, and LED",
        type: "component_count",
        config: { type: "component_count", min: 4 },
        required: true,
        order: 2
      },
      {
        id: "basics_2_led_works",
        description: "Make the LED light up when the switch is closed",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "basics_2_hint_1",
        order: 1,
        content: 'Find the Switch in the "Passive" section of the component palette.',
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_2_hint_2",
        order: 2,
        content: "Place the switch in series with your other components. Current must pass through the switch to reach the LED.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_2_hint_3",
        order: 3,
        content: "Click on the switch component to toggle it open/closed. The switch starts in the OPEN position.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 60,
    bonusXp: 30,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["basics_3"]
  },
  {
    id: "basics_3",
    chapterId: "basics",
    order: 3,
    title: "Ground Reference",
    description: "Learn about ground - the reference point for all circuit voltages.",
    educationalConcept: {
      title: "What is Ground?",
      explanation: "Ground is the reference point (0V) for measuring all voltages in a circuit. It provides a common return path for current. In real circuits, ground often connects to the Earth or a metal chassis.",
      keyTerms: [
        { term: "Ground", definition: "The 0V reference point in a circuit" },
        { term: "Voltage Reference", definition: "A fixed point against which all other voltages are measured" },
        { term: "Return Path", definition: "The route current takes to get back to the power source" }
      ],
      relatedComponents: ["ground", "battery"]
    },
    prerequisiteLevelId: "basics_2",
    objectives: [
      {
        id: "basics_3_add_ground",
        description: "Add a ground symbol to your circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "ground" },
        required: true,
        order: 1
      },
      {
        id: "basics_3_connect_ground",
        description: "Connect ground to the battery negative terminal",
        type: "connect_terminals",
        config: { type: "connect_terminals", fromComponentType: "ground", toComponentType: "battery" },
        required: true,
        order: 2
      },
      {
        id: "basics_3_complete",
        description: "Complete the circuit and light the LED",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "basics_3_hint_1",
        order: 1,
        content: 'The ground symbol is in the "Power" section. It represents 0V.',
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_3_hint_2",
        order: 2,
        content: "Connect the ground to the negative (-) terminal of the battery. This establishes the 0V reference.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 50,
    bonusXp: 25,
    timeBonusThresholdSeconds: 120,
    unlocksLevelIds: ["basics_4"]
  },
  {
    id: "basics_4",
    chapterId: "basics",
    order: 4,
    title: "Two LEDs",
    description: "Power multiple LEDs in your circuit.",
    educationalConcept: {
      title: "Powering Multiple Components",
      explanation: "A single power source can power multiple components! When components share the same power source, you need to plan your connections carefully. Each LED needs its own current-limiting resistor.",
      keyTerms: [
        { term: "Current Path", definition: "The route current takes through a circuit" },
        { term: "Branch", definition: "A separate path in a circuit where current can flow" },
        { term: "Current Limiting", definition: "Using a resistor to prevent too much current from damaging a component" }
      ],
      relatedComponents: ["led", "resistor", "battery"]
    },
    prerequisiteLevelId: "basics_3",
    objectives: [
      {
        id: "basics_4_two_leds",
        description: "Add two LEDs to your circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "led", count: 2 },
        required: true,
        order: 1
      },
      {
        id: "basics_4_two_resistors",
        description: "Add a resistor for each LED",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 2 },
        required: true,
        order: 2
      },
      {
        id: "basics_4_light_both",
        description: "Make both LEDs light up",
        type: "power_multiple_leds",
        config: { type: "power_multiple_leds", count: 2, minBrightness: 0.3 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "basics_4_hint_1",
        order: 1,
        content: "Each LED needs its own resistor to limit current. Without resistors, LEDs will burn out!",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_4_hint_2",
        order: 2,
        content: "Connect both LED-resistor pairs to the same battery. They share the power source but have separate current paths.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 75,
    bonusXp: 40,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["basics_5"]
  },
  {
    id: "basics_5",
    chapterId: "basics",
    order: 5,
    title: "Circuit Safety",
    description: "Learn about protecting circuits with fuses.",
    educationalConcept: {
      title: "Circuit Protection",
      explanation: 'A fuse is a safety device that protects your circuit. If too much current flows (like in a short circuit), the fuse "blows" and breaks the circuit, preventing damage or fire. Every home and car has fuses!',
      keyTerms: [
        { term: "Fuse", definition: "A protective device that melts and breaks the circuit if current is too high" },
        { term: "Overcurrent", definition: "Current that exceeds the safe level for a circuit" },
        { term: "Short Circuit", definition: "An unintended low-resistance path that causes excessive current" }
      ],
      relatedComponents: ["fuse", "battery", "resistor"]
    },
    prerequisiteLevelId: "basics_4",
    objectives: [
      {
        id: "basics_5_add_fuse",
        description: "Add a fuse to protect your circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "fuse" },
        required: true,
        order: 1
      },
      {
        id: "basics_5_build_protected",
        description: "Build a protected circuit with fuse, resistor, and LED",
        type: "component_count",
        config: { type: "component_count", min: 4 },
        required: true,
        order: 2
      },
      {
        id: "basics_5_working",
        description: "Make the circuit work with the fuse intact",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "basics_5_hint_1",
        order: 1,
        content: 'Find the Fuse in the "Protection" section of the component palette.',
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_5_hint_2",
        order: 2,
        content: "Place the fuse in series with your circuit, typically right after the battery positive terminal.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "basics_5_hint_3",
        order: 3,
        content: "The fuse should NOT blow in a properly designed circuit. If it blows, your resistance is too low!",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 80,
    bonusXp: 40,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["ohms_law_1"]
  }
];
var ohmsLawLevels = [
  {
    id: "ohms_law_1",
    chapterId: "ohms_law",
    order: 1,
    title: "The Magic Formula",
    description: "Discover Ohm's Law - the most important formula in electronics!",
    educationalConcept: {
      title: "Ohm's Law: V = I x R",
      explanation: "Ohm's Law describes the relationship between Voltage (V), Current (I), and Resistance (R). If you know any two, you can calculate the third! V = I x R, I = V/R, R = V/I. This is the foundation of all circuit analysis.",
      formula: "V = I \xD7 R",
      formulaDescription: "Voltage (Volts) = Current (Amps) \xD7 Resistance (Ohms)",
      keyTerms: [
        { term: "Voltage (V)", definition: "Electrical pressure that pushes current through a circuit, measured in Volts" },
        { term: "Current (I)", definition: "The flow rate of electrons, measured in Amperes (Amps)" },
        { term: "Resistance (R)", definition: "Opposition to current flow, measured in Ohms (\u03A9)" }
      ],
      relatedComponents: ["battery", "resistor"]
    },
    starterCircuit: {
      components: [
        {
          id: "starter_battery_ohm1",
          type: "battery",
          position: { x: 100, y: 200 },
          rotation: 0,
          terminals: [
            { id: "bat_pos", type: "positive", position: { x: 130, y: 200 }, connectedTo: [] },
            { id: "bat_neg", type: "negative", position: { x: 70, y: 200 }, connectedTo: [] }
          ],
          properties: { voltage: 9 }
        }
      ],
      wires: [],
      lockedComponentIds: ["starter_battery_ohm1"],
      highlightedTerminalIds: ["bat_pos", "bat_neg"]
    },
    objectives: [
      {
        id: "ohms_law_1_add_resistor",
        description: "Add a 1000\u03A9 resistor to the circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor" },
        required: true,
        order: 1
      },
      {
        id: "ohms_law_1_connect",
        description: "Connect the resistor to complete the circuit",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      },
      {
        id: "ohms_law_1_calculate",
        description: "Calculate: With 9V and 1000\u03A9, current = 9mA (V/R)",
        type: "achieve_current",
        config: { type: "achieve_current", componentType: "resistor", targetCurrent: 9e-3, tolerance: 2e-3 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "ohms_law_1_hint_1",
        order: 1,
        content: "The battery is already placed (9V). Add a 1000\u03A9 resistor to see Ohm's Law in action!",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_1_hint_2",
        order: 2,
        content: "Using Ohm's Law: I = V/R = 9V / 1000\u03A9 = 0.009A = 9mA",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_1_hint_3",
        order: 3,
        content: "Open the Formula Calculator (click on the resistor, then Calculator tab) to verify your calculation!",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "voltage_match", tolerancePercent: 10 },
    xpReward: 75,
    bonusXp: 35,
    timeBonusThresholdSeconds: 120,
    unlocksLevelIds: ["ohms_law_2"]
  },
  {
    id: "ohms_law_2",
    chapterId: "ohms_law",
    order: 2,
    title: "Changing Resistance",
    description: "See how changing resistance affects current flow.",
    educationalConcept: {
      title: "Resistance and Current",
      explanation: "According to Ohm's Law (I = V/R), when voltage stays constant, increasing resistance DECREASES current. Double the resistance = half the current. This is why we use resistors to control LED brightness!",
      formula: "I = V / R",
      formulaDescription: "Current is inversely proportional to resistance",
      keyTerms: [
        { term: "Inverse Relationship", definition: "When one value increases, the other decreases" },
        { term: "Current Limiting", definition: "Using resistance to reduce current to a safe level" }
      ],
      relatedComponents: ["resistor", "led", "battery"]
    },
    prerequisiteLevelId: "ohms_law_1",
    objectives: [
      {
        id: "ohms_law_2_circuit",
        description: "Build a circuit with a battery, resistor, and LED",
        type: "component_count",
        config: { type: "component_count", min: 3 },
        required: true,
        order: 1
      },
      {
        id: "ohms_law_2_bright_led",
        description: "Make the LED glow brightly (lower resistance = more current)",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.7 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "ohms_law_2_hint_1",
        order: 1,
        content: "To get more current through the LED, you need LESS resistance. Try a smaller resistor value.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_2_hint_2",
        order: 2,
        content: "Select the resistor and change its resistance value in the properties panel. Try 220\u03A9 or 330\u03A9.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_2_hint_3",
        order: 3,
        content: "Warning: Too little resistance can damage the LED! Keep current under 20mA (0.02A).",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 80,
    bonusXp: 40,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["ohms_law_3"]
  },
  {
    id: "ohms_law_3",
    chapterId: "ohms_law",
    order: 3,
    title: "Voltage Division",
    description: "Learn how voltage divides across components in a circuit.",
    educationalConcept: {
      title: "Voltage Drops",
      explanation: "In a series circuit, the total voltage from the battery is divided among all components. The voltage drop across each component depends on its resistance. Higher resistance = larger voltage drop.",
      formula: "V_total = V1 + V2 + V3...",
      formulaDescription: "The sum of all voltage drops equals the source voltage",
      keyTerms: [
        { term: "Voltage Drop", definition: "The decrease in voltage across a component as current flows through it" },
        { term: "Series Circuit", definition: "A circuit where components are connected end-to-end, sharing the same current" },
        { term: "Kirchhoff's Voltage Law", definition: "The sum of voltages around a closed loop equals zero" }
      ],
      relatedComponents: ["battery", "resistor", "led"]
    },
    prerequisiteLevelId: "ohms_law_2",
    objectives: [
      {
        id: "ohms_law_3_two_resistors",
        description: "Add two resistors in series",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 2 },
        required: true,
        order: 1
      },
      {
        id: "ohms_law_3_complete",
        description: "Complete the circuit and observe how voltage divides",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "ohms_law_3_hint_1",
        order: 1,
        content: "Connect two resistors in series (end to end). Current flows through both sequentially.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_3_hint_2",
        order: 2,
        content: "Use the Simulation Panel to see the voltage drop across each resistor after running the simulation.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_3_hint_3",
        order: 3,
        content: "Try different resistor values! A bigger resistor will have a bigger voltage drop.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "voltage_match", tolerancePercent: 10 },
    xpReward: 85,
    bonusXp: 45,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["ohms_law_4"]
  },
  {
    id: "ohms_law_4",
    chapterId: "ohms_law",
    order: 4,
    title: "Power Calculation",
    description: "Calculate power consumption using P = V x I.",
    educationalConcept: {
      title: "Electrical Power",
      explanation: "Power (P) is the rate at which electrical energy is converted to another form (heat, light, motion). P = V \xD7 I = I\xB2R = V\xB2/R. Power is measured in Watts (W). This is what you pay for on your electric bill!",
      formula: "P = V \xD7 I",
      formulaDescription: "Power (Watts) = Voltage (Volts) \xD7 Current (Amps)",
      keyTerms: [
        { term: "Power (P)", definition: "Rate of energy transfer, measured in Watts (W)" },
        { term: "Watt", definition: "One Joule of energy per second" },
        { term: "Power Dissipation", definition: "Energy released as heat when current flows through resistance" }
      ],
      relatedComponents: ["resistor", "battery", "led"]
    },
    prerequisiteLevelId: "ohms_law_3",
    objectives: [
      {
        id: "ohms_law_4_circuit",
        description: "Build a circuit with a battery and resistor",
        type: "component_count",
        config: { type: "component_count", min: 2 },
        required: true,
        order: 1
      },
      {
        id: "ohms_law_4_simulate",
        description: "Run simulation and check the power reading",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "ohms_law_4_hint_1",
        order: 1,
        content: "Build a simple circuit: 9V battery with a 100\u03A9 resistor.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_4_hint_2",
        order: 2,
        content: "Calculate: I = 9V/100\u03A9 = 0.09A, then P = 9V \xD7 0.09A = 0.81W",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_4_hint_3",
        order: 3,
        content: "Use the Power Calculator in the Formula Calculator panel to verify your calculation!",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 90,
    bonusXp: 45,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["ohms_law_5"]
  },
  {
    id: "ohms_law_5",
    chapterId: "ohms_law",
    order: 5,
    title: "Ohm's Law Challenge",
    description: "Put it all together! Design a circuit to achieve a specific current.",
    educationalConcept: {
      title: "Applying Ohm's Law",
      explanation: "Now you can use Ohm's Law to DESIGN circuits! Given a target current and voltage, calculate the resistance needed: R = V/I. This is how engineers design everything from phone chargers to power supplies.",
      formula: "R = V / I",
      formulaDescription: "Calculate the resistance needed for a target current",
      keyTerms: [
        { term: "Design", definition: "Calculating component values to achieve desired circuit behavior" },
        { term: "Target Current", definition: "The specific current value you want to achieve" },
        { term: "Component Selection", definition: "Choosing the right resistor value for your needs" }
      ],
      relatedComponents: ["resistor", "battery", "led"]
    },
    prerequisiteLevelId: "ohms_law_4",
    objectives: [
      {
        id: "ohms_law_5_target_current",
        description: "Design a circuit that produces exactly 15mA (0.015A) with a 9V battery",
        type: "achieve_current",
        config: { type: "achieve_current", componentType: "resistor", targetCurrent: 0.015, tolerance: 2e-3 },
        required: true,
        order: 1
      },
      {
        id: "ohms_law_5_no_warnings",
        description: "Complete without any circuit warnings",
        type: "no_warnings",
        config: { type: "no_warnings" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "ohms_law_5_hint_1",
        order: 1,
        content: "Target: 15mA (0.015A) with 9V. Use R = V/I to find the resistor value needed.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_5_hint_2",
        order: 2,
        content: "R = 9V / 0.015A = 600\u03A9. Use a 600\u03A9 resistor (or the closest value).",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "ohms_law_5_hint_3",
        order: 3,
        content: "Select the resistor and adjust its value to 600\u03A9 in the properties panel.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 100,
    bonusXp: 50,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["led_circuits_1"]
  }
];
var ledCircuitsLevels = [
  {
    id: "led_circuits_1",
    chapterId: "led_circuits",
    order: 1,
    title: "LED Basics",
    description: "Learn why LEDs need resistors and how to calculate the right value.",
    educationalConcept: {
      title: "LED Forward Voltage",
      explanation: 'LEDs are special - they have a "forward voltage" (Vf) that must be exceeded for them to turn on. A red LED typically has Vf \u2248 2V. The remaining voltage must be dropped across a resistor to limit current.',
      formula: "R = (V_source - V_forward) / I_led",
      formulaDescription: "Calculate the resistor needed to safely power an LED",
      keyTerms: [
        { term: "Forward Voltage (Vf)", definition: "The minimum voltage needed across an LED to make it light up" },
        { term: "LED Current", definition: "Typical LEDs work best at 10-20mA" },
        { term: "Current Limiting Resistor", definition: "A resistor that limits current to protect the LED" }
      ],
      relatedComponents: ["led", "resistor", "battery"]
    },
    objectives: [
      {
        id: "led_circuits_1_build",
        description: "Build a circuit with battery, resistor, and LED",
        type: "component_count",
        config: { type: "component_count", min: 3 },
        required: true,
        order: 1
      },
      {
        id: "led_circuits_1_safe_current",
        description: "Make the LED light up without exceeding 20mA",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.5 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "led_circuits_1_hint_1",
        order: 1,
        content: "For a 9V battery and red LED (Vf=2V), you need: R = (9-2)/0.02 = 350\u03A9 for 20mA",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "led_circuits_1_hint_2",
        order: 2,
        content: "A 330\u03A9 or 470\u03A9 resistor is commonly used. Too much resistance = dim LED, too little = damaged LED.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 75,
    bonusXp: 35,
    timeBonusThresholdSeconds: 120,
    unlocksLevelIds: ["led_circuits_2"]
  },
  {
    id: "led_circuits_2",
    chapterId: "led_circuits",
    order: 2,
    title: "LED Brightness Control",
    description: "Control LED brightness by adjusting current flow.",
    educationalConcept: {
      title: "Brightness and Current",
      explanation: "LED brightness is proportional to current. More current = brighter LED (up to its limit). By changing the resistor value, you can dim or brighten the LED. This is called analog brightness control.",
      formula: "Brightness \u221D Current",
      formulaDescription: "LED brightness is directly proportional to current",
      keyTerms: [
        { term: "Proportional", definition: "When one value increases, the other increases at the same rate" },
        { term: "Analog Control", definition: "Continuous adjustment (vs. digital on/off)" },
        { term: "Current Adjustment", definition: "Changing resistor value to control current and brightness" }
      ],
      relatedComponents: ["led", "resistor"]
    },
    prerequisiteLevelId: "led_circuits_1",
    objectives: [
      {
        id: "led_circuits_2_dim",
        description: "Create a dim LED (use high resistance)",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.2 },
        required: true,
        order: 1
      },
      {
        id: "led_circuits_2_bright",
        description: "Then make it bright (lower resistance)",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.8 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "led_circuits_2_hint_1",
        order: 1,
        content: "For a dim LED, use a high resistance like 2200\u03A9 or more. This limits current to just a few mA.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "led_circuits_2_hint_2",
        order: 2,
        content: "For bright LED, use around 220\u03A9-330\u03A9. This gives about 20mA - the LED's optimal current.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 80,
    bonusXp: 40,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["led_circuits_3"]
  },
  {
    id: "led_circuits_3",
    chapterId: "led_circuits",
    order: 3,
    title: "Multiple LEDs in Series",
    description: "Connect LEDs in series for higher voltage applications.",
    educationalConcept: {
      title: "LEDs in Series",
      explanation: "When LEDs are connected in series, their forward voltages ADD UP. 3 red LEDs in series need 6V to light up! You need a power source voltage greater than the sum of all Vf values.",
      formula: "V_total = Vf1 + Vf2 + Vf3...",
      formulaDescription: "Total voltage drop = sum of individual LED forward voltages",
      keyTerms: [
        { term: "Series Connection", definition: "Components connected end-to-end, same current flows through all" },
        { term: "Voltage Addition", definition: "In series, voltages add up" },
        { term: "Shared Current", definition: "All components in series have the same current" }
      ],
      relatedComponents: ["led", "resistor", "battery"]
    },
    prerequisiteLevelId: "led_circuits_2",
    objectives: [
      {
        id: "led_circuits_3_two_series",
        description: "Connect 2 LEDs in series",
        type: "add_component",
        config: { type: "add_component", componentType: "led", count: 2 },
        required: true,
        order: 1
      },
      {
        id: "led_circuits_3_light_both",
        description: "Make both LEDs light up equally",
        type: "power_multiple_leds",
        config: { type: "power_multiple_leds", count: 2, minBrightness: 0.4 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "led_circuits_3_hint_1",
        order: 1,
        content: "Connect LED1 positive to LED2 negative. They share the same current path.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "led_circuits_3_hint_2",
        order: 2,
        content: "For 2 LEDs (4V total Vf) with 9V: R = (9-4)/0.015 = 333\u03A9",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 90,
    bonusXp: 45,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["led_circuits_4"]
  },
  {
    id: "led_circuits_4",
    chapterId: "led_circuits",
    order: 4,
    title: "Multiple LEDs in Parallel",
    description: "Power multiple LEDs with separate resistors.",
    educationalConcept: {
      title: "LEDs in Parallel",
      explanation: "Each LED in a parallel configuration needs its own current-limiting resistor! Never connect LEDs directly in parallel - slight differences in Vf cause uneven current distribution and damage.",
      formula: "I_total = I1 + I2 + I3...",
      formulaDescription: "Total current = sum of current through each LED",
      keyTerms: [
        { term: "Parallel Connection", definition: "Components connected between the same two points" },
        { term: "Current Division", definition: "Total current splits among parallel branches" },
        { term: "Individual Resistors", definition: "Each parallel LED needs its own current limiter" }
      ],
      relatedComponents: ["led", "resistor", "battery"]
    },
    prerequisiteLevelId: "led_circuits_3",
    objectives: [
      {
        id: "led_circuits_4_parallel_leds",
        description: "Add 3 LEDs in parallel branches",
        type: "add_component",
        config: { type: "add_component", componentType: "led", count: 3 },
        required: true,
        order: 1
      },
      {
        id: "led_circuits_4_resistors",
        description: "Add a resistor for each LED",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 3 },
        required: true,
        order: 2
      },
      {
        id: "led_circuits_4_all_lit",
        description: "Light all 3 LEDs",
        type: "power_multiple_leds",
        config: { type: "power_multiple_leds", count: 3, minBrightness: 0.4 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "led_circuits_4_hint_1",
        order: 1,
        content: "Create 3 separate branches from the battery. Each branch: resistor \u2192 LED.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "led_circuits_4_hint_2",
        order: 2,
        content: "All branches connect to the same battery terminals, but have separate current paths.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 95,
    bonusXp: 50,
    timeBonusThresholdSeconds: 200,
    unlocksLevelIds: ["led_circuits_5"]
  },
  {
    id: "led_circuits_5",
    chapterId: "led_circuits",
    order: 5,
    title: "LED Challenge",
    description: "Design a circuit with both series and parallel LEDs!",
    educationalConcept: {
      title: "Combined Configurations",
      explanation: "Real circuits often combine series and parallel. You might have 2 series pairs, each pair in parallel with the other. This balances voltage requirements with current distribution.",
      keyTerms: [
        { term: "Series-Parallel", definition: "A combination of series and parallel connections" },
        { term: "Branch", definition: "A separate current path in a parallel circuit" },
        { term: "Balanced Design", definition: "Ensuring all LEDs receive appropriate current" }
      ],
      relatedComponents: ["led", "resistor", "battery"]
    },
    prerequisiteLevelId: "led_circuits_4",
    objectives: [
      {
        id: "led_circuits_5_four_leds",
        description: "Create a circuit with 4 LEDs",
        type: "add_component",
        config: { type: "add_component", componentType: "led", count: 4 },
        required: true,
        order: 1
      },
      {
        id: "led_circuits_5_all_lit",
        description: "Light all 4 LEDs",
        type: "power_multiple_leds",
        config: { type: "power_multiple_leds", count: 4, minBrightness: 0.4 },
        required: true,
        order: 2
      },
      {
        id: "led_circuits_5_efficient",
        description: "Use minimum components (hint: series-parallel)",
        type: "no_warnings",
        config: { type: "no_warnings" },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "led_circuits_5_hint_1",
        order: 1,
        content: "Try 2 pairs of series LEDs, with each pair in parallel. This uses only 2 resistors!",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "led_circuits_5_hint_2",
        order: 2,
        content: "Branch 1: Resistor \u2192 LED1 \u2192 LED2. Branch 2: Resistor \u2192 LED3 \u2192 LED4. Both share battery.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 110,
    bonusXp: 55,
    timeBonusThresholdSeconds: 240,
    unlocksLevelIds: ["series_parallel_1"]
  }
];
var seriesParallelLevels = [
  {
    id: "series_parallel_1",
    chapterId: "series_parallel",
    order: 1,
    title: "Resistors in Series",
    description: "Learn how resistances add up in series.",
    educationalConcept: {
      title: "Series Resistance",
      explanation: "When resistors are in series, their resistances simply ADD together. R_total = R1 + R2 + R3. The same current flows through all of them, but voltage divides among them.",
      formula: "R_total = R1 + R2 + R3...",
      formulaDescription: "Total resistance = sum of individual resistances",
      keyTerms: [
        { term: "Series Addition", definition: "Resistances in series add directly" },
        { term: "Same Current", definition: "All series components share the same current" },
        { term: "Voltage Division", definition: "Voltage splits according to resistance ratio" }
      ],
      relatedComponents: ["resistor", "battery"]
    },
    objectives: [
      {
        id: "series_parallel_1_three_resistors",
        description: "Connect 3 resistors in series",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 3 },
        required: true,
        order: 1
      },
      {
        id: "series_parallel_1_simulate",
        description: "Verify total resistance in simulation",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "series_parallel_1_hint_1",
        order: 1,
        content: "Connect resistors end-to-end: Battery(+) \u2192 R1 \u2192 R2 \u2192 R3 \u2192 Battery(-)",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "series_parallel_1_hint_2",
        order: 2,
        content: "3 x 1000\u03A9 in series = 3000\u03A9 total. Current = 9V/3000\u03A9 = 3mA",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 75,
    bonusXp: 35,
    timeBonusThresholdSeconds: 120,
    unlocksLevelIds: ["series_parallel_2"]
  },
  {
    id: "series_parallel_2",
    chapterId: "series_parallel",
    order: 2,
    title: "Resistors in Parallel",
    description: "Discover how parallel resistors combine differently.",
    educationalConcept: {
      title: "Parallel Resistance",
      explanation: "Parallel resistors are trickier! 1/R_total = 1/R1 + 1/R2. For two equal resistors in parallel, the total is HALF of one. More paths = easier for current = less total resistance.",
      formula: "1/R_total = 1/R1 + 1/R2",
      formulaDescription: "Reciprocals add, then take reciprocal of result",
      keyTerms: [
        { term: "Parallel Resistance", definition: "Total is always less than the smallest individual resistor" },
        { term: "Current Splitting", definition: "Total current divides among parallel paths" },
        { term: "Same Voltage", definition: "All parallel components have the same voltage across them" }
      ],
      relatedComponents: ["resistor", "battery"]
    },
    prerequisiteLevelId: "series_parallel_1",
    objectives: [
      {
        id: "series_parallel_2_parallel_resistors",
        description: "Connect 2 resistors in parallel",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 2 },
        required: true,
        order: 1
      },
      {
        id: "series_parallel_2_observe",
        description: "Run simulation and observe current through each",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "series_parallel_2_hint_1",
        order: 1,
        content: "Connect both resistors between the same two points - they share both battery terminals.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "series_parallel_2_hint_2",
        order: 2,
        content: "2 x 1000\u03A9 in parallel = 500\u03A9. For equal resistors: R_parallel = R/n",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 85,
    bonusXp: 40,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["series_parallel_3"]
  },
  {
    id: "series_parallel_3",
    chapterId: "series_parallel",
    order: 3,
    title: "Voltage Divider",
    description: "Build a practical voltage divider circuit.",
    educationalConcept: {
      title: "Voltage Dividers",
      explanation: "A voltage divider uses two resistors in series to create a specific output voltage. V_out = V_in \xD7 (R2/(R1+R2)). This is used everywhere - from sensor circuits to phone chargers!",
      formula: "V_out = V_in \xD7 (R2 / (R1 + R2))",
      formulaDescription: "Output voltage based on resistor ratio",
      keyTerms: [
        { term: "Voltage Divider", definition: "Two resistors that create a fractional voltage output" },
        { term: "Output Tap", definition: "The point between resistors where output is measured" },
        { term: "Division Ratio", definition: "The fraction of input voltage that appears at output" }
      ],
      relatedComponents: ["resistor", "battery"]
    },
    prerequisiteLevelId: "series_parallel_2",
    objectives: [
      {
        id: "series_parallel_3_divider",
        description: "Build a voltage divider with 2 resistors",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 2 },
        required: true,
        order: 1
      },
      {
        id: "series_parallel_3_half_voltage",
        description: "Create a 50% voltage divider (equal resistors)",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "series_parallel_3_hint_1",
        order: 1,
        content: "Use two equal resistors. With 9V in, you'll get 4.5V at the midpoint.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "series_parallel_3_hint_2",
        order: 2,
        content: "For different ratios, change R2. More R2 = higher output voltage.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 90,
    bonusXp: 45,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["series_parallel_4"]
  },
  {
    id: "series_parallel_4",
    chapterId: "series_parallel",
    order: 4,
    title: "Current Divider",
    description: "Build a current divider and understand current distribution.",
    educationalConcept: {
      title: "Current Division",
      explanation: "In parallel circuits, current divides inversely to resistance. The path with LESS resistance gets MORE current. I1/I2 = R2/R1. Current takes the path of least resistance!",
      formula: "I1 = I_total \xD7 (R2 / (R1 + R2))",
      formulaDescription: "Current in R1 based on opposite resistor ratio",
      keyTerms: [
        { term: "Current Division", definition: "Current splits inversely proportional to resistance" },
        { term: "Path of Least Resistance", definition: "Current prefers lower resistance paths" },
        { term: "Branch Current", definition: "Current flowing through one parallel branch" }
      ],
      relatedComponents: ["resistor", "battery"]
    },
    prerequisiteLevelId: "series_parallel_3",
    objectives: [
      {
        id: "series_parallel_4_unequal",
        description: "Connect 2 unequal resistors in parallel",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 2 },
        required: true,
        order: 1
      },
      {
        id: "series_parallel_4_observe_current",
        description: "Observe how current divides between paths",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "series_parallel_4_hint_1",
        order: 1,
        content: "Use 1000\u03A9 and 2000\u03A9 in parallel. The 1k\u03A9 will carry twice the current!",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "series_parallel_4_hint_2",
        order: 2,
        content: "Run simulation and compare the current values shown for each resistor.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 95,
    bonusXp: 50,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["series_parallel_5"]
  },
  {
    id: "series_parallel_5",
    chapterId: "series_parallel",
    order: 5,
    title: "Complex Network",
    description: "Analyze a series-parallel combination circuit.",
    educationalConcept: {
      title: "Series-Parallel Analysis",
      explanation: "Complex circuits combine series and parallel. Solve by simplifying step-by-step: 1) Find parallel groups and combine them 2) Add series resistances 3) Repeat until you have one equivalent resistance.",
      keyTerms: [
        { term: "Equivalent Resistance", definition: "A single resistor that has the same effect as the combination" },
        { term: "Simplification", definition: "Reducing a complex circuit to simpler form" },
        { term: "Step-by-Step Analysis", definition: "Breaking down complex problems into simple steps" }
      ],
      relatedComponents: ["resistor", "battery"]
    },
    prerequisiteLevelId: "series_parallel_4",
    objectives: [
      {
        id: "series_parallel_5_complex",
        description: "Build a circuit with 4+ resistors in series-parallel",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 4 },
        required: true,
        order: 1
      },
      {
        id: "series_parallel_5_working",
        description: "Complete the circuit and run simulation",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "series_parallel_5_hint_1",
        order: 1,
        content: "Try: R1 in series with (R2 || R3) in series with R4. || means parallel.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "series_parallel_5_hint_2",
        order: 2,
        content: "To calculate: First find R2||R3, then add R1 + (R2||R3) + R4.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 110,
    bonusXp: 55,
    timeBonusThresholdSeconds: 240,
    unlocksLevelIds: ["logic_gates_1"]
  }
];
var logicGatesLevels = [
  {
    id: "logic_gates_1",
    chapterId: "logic_gates",
    order: 1,
    title: "Meet the AND Gate",
    description: "Discover the AND gate - output is HIGH only when ALL inputs are HIGH.",
    educationalConcept: {
      title: "AND Gate",
      explanation: "An AND gate outputs HIGH (1) only when ALL inputs are HIGH. If any input is LOW, output is LOW. Think of it as a series of switches - all must be ON for current to flow. A AND B = output.",
      keyTerms: [
        { term: "AND Gate", definition: "Output is 1 only if all inputs are 1" },
        { term: "HIGH/LOW", definition: "Digital signals: HIGH=1=ON, LOW=0=OFF" },
        { term: "Truth Table", definition: "A table showing all input combinations and their outputs" }
      ],
      relatedComponents: ["and_gate", "switch", "led"]
    },
    objectives: [
      {
        id: "logic_gates_1_add_and",
        description: "Add an AND gate to the circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "and_gate" },
        required: true,
        order: 1
      },
      {
        id: "logic_gates_1_inputs",
        description: "Connect two switches to the AND gate inputs",
        type: "add_component",
        config: { type: "add_component", componentType: "switch", count: 2 },
        required: true,
        order: 2
      },
      {
        id: "logic_gates_1_output",
        description: "Connect the output to an LED",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "logic_gates_1_hint_1",
        order: 1,
        content: 'Find the AND gate in the "Logic" section. It has 2 inputs (A, B) and 1 output.',
        triggerCondition: { type: "user_request" }
      },
      {
        id: "logic_gates_1_hint_2",
        order: 2,
        content: "Connect switches to input_a and input_b. The LED lights only when BOTH switches are closed.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 80,
    bonusXp: 40,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["logic_gates_2"]
  },
  {
    id: "logic_gates_2",
    chapterId: "logic_gates",
    order: 2,
    title: "The OR Gate",
    description: "Learn the OR gate - output is HIGH when ANY input is HIGH.",
    educationalConcept: {
      title: "OR Gate",
      explanation: "An OR gate outputs HIGH if ANY input is HIGH. Only when ALL inputs are LOW does it output LOW. Think of parallel switches - any one can complete the circuit. A OR B = output.",
      keyTerms: [
        { term: "OR Gate", definition: "Output is 1 if any input is 1" },
        { term: "Inclusive OR", definition: "True if one OR the other OR both are true" },
        { term: "Parallel Logic", definition: "Any path can activate the output" }
      ],
      relatedComponents: ["or_gate", "switch", "led"]
    },
    prerequisiteLevelId: "logic_gates_1",
    objectives: [
      {
        id: "logic_gates_2_add_or",
        description: "Add an OR gate to the circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "or_gate" },
        required: true,
        order: 1
      },
      {
        id: "logic_gates_2_test",
        description: "Test that either switch lights the LED",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "logic_gates_2_hint_1",
        order: 1,
        content: 'The OR gate is also in "Logic". With either input HIGH, the output is HIGH.',
        triggerCondition: { type: "user_request" }
      },
      {
        id: "logic_gates_2_hint_2",
        order: 2,
        content: "Test by closing just switch A, then just switch B - LED should light in both cases.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 80,
    bonusXp: 40,
    timeBonusThresholdSeconds: 150,
    unlocksLevelIds: ["logic_gates_3"]
  },
  {
    id: "logic_gates_3",
    chapterId: "logic_gates",
    order: 3,
    title: "The NOT Gate (Inverter)",
    description: "Meet the NOT gate - it flips the input signal.",
    educationalConcept: {
      title: "NOT Gate (Inverter)",
      explanation: "A NOT gate inverts its input: HIGH becomes LOW, LOW becomes HIGH. It has only one input. NOT gates are fundamental - combined with AND or OR, you can create any logic function!",
      keyTerms: [
        { term: "NOT Gate", definition: "Output is the opposite of the input" },
        { term: "Inverter", definition: "Another name for NOT gate" },
        { term: "Complement", definition: "The inverted value (opposite)" }
      ],
      relatedComponents: ["not_gate", "switch", "led"]
    },
    prerequisiteLevelId: "logic_gates_2",
    objectives: [
      {
        id: "logic_gates_3_add_not",
        description: "Add a NOT gate",
        type: "add_component",
        config: { type: "add_component", componentType: "not_gate" },
        required: true,
        order: 1
      },
      {
        id: "logic_gates_3_test_invert",
        description: "Make LED turn OFF when switch is ON",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "logic_gates_3_hint_1",
        order: 1,
        content: "NOT gate has 1 input and 1 output. Connect switch \u2192 NOT \u2192 LED.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "logic_gates_3_hint_2",
        order: 2,
        content: "With switch OPEN (LOW input), output is HIGH and LED lights. Close switch = LED off.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 75,
    bonusXp: 35,
    timeBonusThresholdSeconds: 120,
    unlocksLevelIds: ["logic_gates_4"]
  },
  {
    id: "logic_gates_4",
    chapterId: "logic_gates",
    order: 4,
    title: "NAND Gate (Combination)",
    description: "Combine NOT and AND to create a NAND gate.",
    educationalConcept: {
      title: "NAND Gate",
      explanation: `NAND = NOT + AND. It outputs LOW only when ALL inputs are HIGH (opposite of AND). NAND is special: it's a "universal gate" - you can build ANY logic circuit using only NAND gates!`,
      keyTerms: [
        { term: "NAND Gate", definition: "NOT-AND: output is LOW only when all inputs are HIGH" },
        { term: "Universal Gate", definition: "Can be used to create any other logic gate" },
        { term: "Gate Combination", definition: "Building complex logic from simpler gates" }
      ],
      relatedComponents: ["and_gate", "not_gate", "led"]
    },
    prerequisiteLevelId: "logic_gates_3",
    objectives: [
      {
        id: "logic_gates_4_build_nand",
        description: "Build a NAND using AND and NOT gates",
        type: "add_component",
        config: { type: "add_component", componentType: "and_gate" },
        required: true,
        order: 1
      },
      {
        id: "logic_gates_4_add_not",
        description: "Add a NOT gate after the AND",
        type: "add_component",
        config: { type: "add_component", componentType: "not_gate" },
        required: true,
        order: 2
      },
      {
        id: "logic_gates_4_verify",
        description: "Verify: LED is OFF only when both inputs are HIGH",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "logic_gates_4_hint_1",
        order: 1,
        content: "Connect: Switches \u2192 AND gate \u2192 NOT gate \u2192 LED",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "logic_gates_4_hint_2",
        order: 2,
        content: "Test all 4 combinations: 00\u2192ON, 01\u2192ON, 10\u2192ON, 11\u2192OFF",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 90,
    bonusXp: 45,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["logic_gates_5"]
  },
  {
    id: "logic_gates_5",
    chapterId: "logic_gates",
    order: 5,
    title: "Logic Challenge: XOR",
    description: "Build an XOR gate - output HIGH when inputs DIFFER.",
    educationalConcept: {
      title: "XOR Gate (Exclusive OR)",
      explanation: `XOR outputs HIGH when inputs are DIFFERENT (one HIGH, one LOW). It's "exclusive" - either one but NOT both. XOR is used in arithmetic circuits and encryption!`,
      formula: "XOR = (A AND NOT B) OR (NOT A AND B)",
      formulaDescription: "XOR using basic gates",
      keyTerms: [
        { term: "XOR Gate", definition: "Output is 1 when inputs are different" },
        { term: "Exclusive OR", definition: "True if one or the other, but not both" },
        { term: "Complex Logic", definition: "Combining multiple gates to achieve a function" }
      ],
      relatedComponents: ["and_gate", "or_gate", "not_gate", "led"]
    },
    prerequisiteLevelId: "logic_gates_4",
    objectives: [
      {
        id: "logic_gates_5_build_xor",
        description: "Build an XOR using AND, OR, and NOT gates",
        type: "component_count",
        config: { type: "component_count", min: 5 },
        required: true,
        order: 1
      },
      {
        id: "logic_gates_5_test_xor",
        description: "LED ON when exactly one switch is closed",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "logic_gates_5_hint_1",
        order: 1,
        content: "XOR = (A AND !B) OR (!A AND B). You need 2 AND gates, 2 NOT gates, and 1 OR gate.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "logic_gates_5_hint_2",
        order: 2,
        content: "Path 1: A \u2192 AND with NOT B. Path 2: NOT A \u2192 AND with B. Both paths \u2192 OR \u2192 LED.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 120,
    bonusXp: 60,
    timeBonusThresholdSeconds: 300,
    unlocksLevelIds: ["advanced_1"]
  }
];
var advancedLevels = [
  {
    id: "advanced_1",
    chapterId: "advanced",
    order: 1,
    title: "Transistor Basics",
    description: "Meet the transistor - the building block of modern electronics.",
    educationalConcept: {
      title: "NPN Transistor",
      explanation: "A transistor is like an electronic switch controlled by current. A small current at the Base controls a large current from Collector to Emitter. This is amplification! Transistors power every computer chip.",
      keyTerms: [
        { term: "Transistor", definition: "A semiconductor device that amplifies or switches signals" },
        { term: "Base", definition: "The control terminal that turns the transistor on/off" },
        { term: "Collector/Emitter", definition: "The main current path controlled by the base" },
        { term: "Amplification", definition: "Using small signal to control larger signal" }
      ],
      relatedComponents: ["transistor", "resistor", "led"]
    },
    objectives: [
      {
        id: "advanced_1_add_transistor",
        description: "Add an NPN transistor",
        type: "add_component",
        config: { type: "add_component", componentType: "transistor" },
        required: true,
        order: 1
      },
      {
        id: "advanced_1_switch_led",
        description: "Use transistor to switch an LED on/off",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.5 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "advanced_1_hint_1",
        order: 1,
        content: "Connect: Battery(+) \u2192 LED \u2192 Collector. Emitter \u2192 Ground. Base needs a resistor from a switch.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "advanced_1_hint_2",
        order: 2,
        content: "The base resistor limits current. Try 10k\u03A9. A small base current turns on the transistor.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "power_led" },
    xpReward: 100,
    bonusXp: 50,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["advanced_2"]
  },
  {
    id: "advanced_2",
    chapterId: "advanced",
    order: 2,
    title: "Capacitor Charging",
    description: "Learn how capacitors store and release energy.",
    educationalConcept: {
      title: "Capacitors",
      explanation: "Capacitors store electrical energy like tiny rechargeable batteries. They charge up when connected to a voltage source, then can release that energy. Charging time depends on capacitance and resistance: \u03C4 = R \xD7 C.",
      formula: "\u03C4 = R \xD7 C",
      formulaDescription: "Time constant (seconds) = Resistance (ohms) \xD7 Capacitance (farads)",
      keyTerms: [
        { term: "Capacitor", definition: "Stores electrical energy in an electric field" },
        { term: "Capacitance", definition: "Ability to store charge, measured in Farads (F)" },
        { term: "Time Constant", definition: "Time to charge to 63% of final value" }
      ],
      relatedComponents: ["capacitor", "resistor", "battery"]
    },
    prerequisiteLevelId: "advanced_1",
    objectives: [
      {
        id: "advanced_2_add_capacitor",
        description: "Add a capacitor to the circuit",
        type: "add_component",
        config: { type: "add_component", componentType: "capacitor" },
        required: true,
        order: 1
      },
      {
        id: "advanced_2_rc_circuit",
        description: "Build an RC (resistor-capacitor) circuit",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "advanced_2_hint_1",
        order: 1,
        content: "Connect: Battery(+) \u2192 Resistor \u2192 Capacitor \u2192 Battery(-). This is an RC circuit.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "advanced_2_hint_2",
        order: 2,
        content: "Use the oscilloscope view to see the capacitor voltage rise over time!",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 100,
    bonusXp: 50,
    timeBonusThresholdSeconds: 180,
    unlocksLevelIds: ["advanced_3"]
  },
  {
    id: "advanced_3",
    chapterId: "advanced",
    order: 3,
    title: "LED Blinker",
    description: "Combine components to make an LED that blinks automatically.",
    educationalConcept: {
      title: "Timing Circuits",
      explanation: "By combining capacitors, resistors, and transistors, you can create circuits that oscillate between states automatically. This is the basis of clocks, timers, and blinking lights!",
      keyTerms: [
        { term: "Oscillator", definition: "A circuit that repeatedly switches between states" },
        { term: "Astable", definition: "A circuit with no stable state - always switching" },
        { term: "Frequency", definition: "How fast the circuit oscillates" }
      ],
      relatedComponents: ["capacitor", "transistor", "resistor", "led"]
    },
    prerequisiteLevelId: "advanced_2",
    objectives: [
      {
        id: "advanced_3_oscillator",
        description: "Build an oscillator circuit",
        type: "component_count",
        config: { type: "component_count", min: 5 },
        required: true,
        order: 1
      },
      {
        id: "advanced_3_blinks",
        description: "Make the LED blink",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "advanced_3_hint_1",
        order: 1,
        content: "A basic blinker uses: capacitor charging \u2192 triggers transistor \u2192 capacitor discharges \u2192 repeat.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "advanced_3_hint_2",
        order: 2,
        content: "Change the resistor or capacitor value to adjust blink speed. Larger RC = slower blink.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 120,
    bonusXp: 60,
    timeBonusThresholdSeconds: 300,
    unlocksLevelIds: ["advanced_4"]
  },
  {
    id: "advanced_4",
    chapterId: "advanced",
    order: 4,
    title: "Transistor Amplifier",
    description: "Use a transistor to amplify a weak signal.",
    educationalConcept: {
      title: "Signal Amplification",
      explanation: "Transistors can amplify signals - a small input signal at the base creates a larger output signal at the collector. The gain (\u03B2) determines how much amplification occurs. This is how audio amplifiers work!",
      formula: "I_collector = \u03B2 \xD7 I_base",
      formulaDescription: "Collector current = gain \xD7 base current",
      keyTerms: [
        { term: "Amplifier", definition: "A circuit that increases signal strength" },
        { term: "Gain (\u03B2)", definition: "The amplification factor of a transistor" },
        { term: "Signal", definition: "Varying voltage or current carrying information" }
      ],
      relatedComponents: ["transistor", "resistor", "capacitor"]
    },
    prerequisiteLevelId: "advanced_3",
    objectives: [
      {
        id: "advanced_4_amplifier",
        description: "Build a common-emitter amplifier",
        type: "add_component",
        config: { type: "add_component", componentType: "transistor" },
        required: true,
        order: 1
      },
      {
        id: "advanced_4_biasing",
        description: "Add proper biasing resistors",
        type: "add_component",
        config: { type: "add_component", componentType: "resistor", count: 2 },
        required: true,
        order: 2
      },
      {
        id: "advanced_4_working",
        description: "Verify the circuit amplifies the input",
        type: "simulation_success",
        config: { type: "simulation_success" },
        required: true,
        order: 3
      }
    ],
    hints: [
      {
        id: "advanced_4_hint_1",
        order: 1,
        content: "A common-emitter amplifier: input at base (with coupling capacitor), output at collector.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "advanced_4_hint_2",
        order: 2,
        content: "You need bias resistors to set the operating point. Collector resistor limits current.",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 130,
    bonusXp: 65,
    timeBonusThresholdSeconds: 300,
    unlocksLevelIds: ["advanced_5"]
  },
  {
    id: "advanced_5",
    chapterId: "advanced",
    order: 5,
    title: "Final Challenge: Light-Controlled Circuit",
    description: "Build a circuit that responds to light levels.",
    educationalConcept: {
      title: "Sensor Circuits",
      explanation: "Real-world electronics often use sensors to detect the environment. A light-dependent resistor (LDR) changes resistance based on light. Combined with a transistor, you can create automatic night lights!",
      keyTerms: [
        { term: "Sensor", definition: "A component that responds to physical conditions" },
        { term: "LDR", definition: "Light Dependent Resistor - resistance decreases with more light" },
        { term: "Threshold", definition: "The point at which the circuit changes state" },
        { term: "Automatic Control", definition: "Circuit responds to environment without human input" }
      ],
      relatedComponents: ["potentiometer", "transistor", "resistor", "led"]
    },
    prerequisiteLevelId: "advanced_4",
    objectives: [
      {
        id: "advanced_5_sensor_circuit",
        description: "Build a light-sensitive circuit",
        type: "component_count",
        config: { type: "component_count", min: 4 },
        required: true,
        order: 1
      },
      {
        id: "advanced_5_automatic",
        description: "Make LED turn on automatically in darkness",
        type: "power_led",
        config: { type: "power_led", minBrightness: 0.3 },
        required: true,
        order: 2
      }
    ],
    hints: [
      {
        id: "advanced_5_hint_1",
        order: 1,
        content: "Create a voltage divider with LDR and resistor. Use the midpoint to control transistor base.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "advanced_5_hint_2",
        order: 2,
        content: "When dark, LDR resistance increases, more voltage at divider midpoint turns on transistor and LED.",
        triggerCondition: { type: "user_request" }
      },
      {
        id: "advanced_5_hint_3",
        order: 3,
        content: "Congratulations! You've mastered circuit fundamentals. Keep experimenting and building!",
        triggerCondition: { type: "user_request" }
      }
    ],
    target: { type: "custom" },
    xpReward: 150,
    bonusXp: 75,
    timeBonusThresholdSeconds: 360
  }
];
var TUTORIAL_CHAPTERS = [
  {
    id: "basics",
    title: "Circuit Basics",
    description: "Learn the fundamentals of electrical circuits - what they are, how they work, and how to build them.",
    icon: "zap",
    color: "green",
    educationalConcepts: ["Complete circuits", "Current flow", "Switches", "Ground", "Circuit protection"],
    levels: basicsLevels
  },
  {
    id: "ohms_law",
    title: "Ohm's Law",
    description: "Master the most important formula in electronics: V = I \xD7 R. Learn to calculate voltage, current, and resistance.",
    icon: "calculator",
    color: "blue",
    prerequisiteChapterId: "basics",
    educationalConcepts: ["Voltage", "Current", "Resistance", "Power", "Circuit analysis"],
    levels: ohmsLawLevels
  },
  {
    id: "led_circuits",
    title: "LED Circuits",
    description: "Design LED circuits with proper current limiting. Learn about forward voltage and safe operation.",
    icon: "lightbulb",
    color: "yellow",
    prerequisiteChapterId: "ohms_law",
    educationalConcepts: ["Forward voltage", "Current limiting", "LED brightness", "Multiple LEDs"],
    levels: ledCircuitsLevels
  },
  {
    id: "series_parallel",
    title: "Series & Parallel",
    description: "Understand how components combine in series and parallel configurations.",
    icon: "git-branch",
    color: "purple",
    prerequisiteChapterId: "led_circuits",
    educationalConcepts: ["Series resistance", "Parallel resistance", "Current division", "Voltage division"],
    levels: seriesParallelLevels
  },
  {
    id: "logic_gates",
    title: "Logic Gates",
    description: "Enter the digital world! Learn how AND, OR, and NOT gates process binary signals.",
    icon: "cpu",
    color: "cyan",
    prerequisiteChapterId: "series_parallel",
    educationalConcepts: ["Binary logic", "Truth tables", "AND gate", "OR gate", "NOT gate", "Combinations"],
    levels: logicGatesLevels
  },
  {
    id: "advanced",
    title: "Advanced Circuits",
    description: "Explore transistors, capacitors, and complex circuit designs.",
    icon: "rocket",
    color: "red",
    prerequisiteChapterId: "logic_gates",
    educationalConcepts: ["Transistor switching", "Capacitor charging", "Timing circuits", "Amplification"],
    levels: advancedLevels
  }
];
function getChapterById(chapterId) {
  return TUTORIAL_CHAPTERS.find((c) => c.id === chapterId);
}
function getLevelById(levelId) {
  for (const chapter of TUTORIAL_CHAPTERS) {
    const level = chapter.levels.find((l) => l.id === levelId);
    if (level) return level;
  }
  return void 0;
}
function getAllLevels() {
  return TUTORIAL_CHAPTERS.flatMap((c) => c.levels);
}
function getTotalLevelCount() {
  return TUTORIAL_CHAPTERS.reduce((sum, c) => sum + c.levels.length, 0);
}
function getLevelsForChapter(chapterId) {
  const chapter = getChapterById(chapterId);
  return chapter?.levels ?? [];
}

// src/data/learningPaths.ts
var SKILLS = [
  // Basic Skills (K-2, 3-5)
  {
    id: "skill-circuit-basics",
    name: "Circuit Basics",
    category: "physics",
    description: "Understanding what makes a complete circuit",
    gradeLevel: "K-2",
    masteryLevel: 0,
    relatedConcepts: ["current", "voltage", "complete-circuit"]
  },
  {
    id: "skill-conductors-insulators",
    name: "Conductors & Insulators",
    category: "physics",
    description: "Identifying materials that conduct electricity",
    gradeLevel: "3-5",
    masteryLevel: 0,
    relatedConcepts: ["materials", "resistance", "safety"]
  },
  {
    id: "skill-switches",
    name: "Switches & Control",
    category: "physics",
    description: "Using switches to control circuit flow",
    gradeLevel: "3-5",
    masteryLevel: 0,
    relatedConcepts: ["open-circuit", "closed-circuit", "control"]
  },
  // Intermediate Skills (6-8)
  {
    id: "skill-ohms-law",
    name: "Ohm's Law",
    category: "physics",
    description: "Applying V = I \xD7 R to circuit analysis",
    gradeLevel: "6-8",
    masteryLevel: 0,
    relatedConcepts: ["voltage", "current", "resistance", "formulas"]
  },
  {
    id: "skill-series-parallel",
    name: "Series & Parallel",
    category: "physics",
    description: "Analyzing series and parallel circuits",
    gradeLevel: "6-8",
    masteryLevel: 0,
    relatedConcepts: ["resistors", "current-division", "voltage-division"]
  },
  {
    id: "skill-power-calculation",
    name: "Power Calculation",
    category: "physics",
    description: "Calculating electrical power using P = V \xD7 I",
    gradeLevel: "6-8",
    masteryLevel: 0,
    relatedConcepts: ["watts", "energy", "efficiency"]
  },
  // Advanced Skills (9-12)
  {
    id: "skill-logic-gates",
    name: "Digital Logic",
    category: "technology",
    description: "Understanding AND, OR, NOT gates and truth tables",
    gradeLevel: "9-12",
    masteryLevel: 0,
    relatedConcepts: ["binary", "boolean-algebra", "digital-circuits"]
  },
  {
    id: "skill-transistors",
    name: "Transistors",
    category: "engineering",
    description: "Using transistors as switches and amplifiers",
    gradeLevel: "9-12",
    masteryLevel: 0,
    relatedConcepts: ["semiconductors", "amplification", "switching"]
  },
  {
    id: "skill-capacitors",
    name: "Capacitors & Timing",
    category: "engineering",
    description: "Understanding capacitor charging and RC circuits",
    gradeLevel: "9-12",
    masteryLevel: 0,
    relatedConcepts: ["energy-storage", "time-constant", "filtering"]
  }
];
var CERTIFICATES = [
  {
    id: "cert-circuit-explorer",
    pathId: "circuits-explorers",
    title: "Circuit Explorer",
    description: "Completed introductory exploration of electrical circuits",
    badgeIcon: "\u{1F50C}",
    badgeColor: "green",
    skills: ["Basic circuit concepts", "Simple connections", "Switch operation"],
    level: "beginner"
  },
  {
    id: "cert-electricity-fundamentals",
    pathId: "electricity-basics",
    title: "Electricity Fundamentals",
    description: "Demonstrated understanding of basic electricity principles",
    badgeIcon: "\u26A1",
    badgeColor: "yellow",
    skills: ["Complete circuits", "Series connections", "LED circuits"],
    level: "beginner"
  },
  {
    id: "cert-ohms-law-master",
    pathId: "ohms-law-mastery",
    title: "Ohm's Law Master",
    description: "Mastered Ohm's Law and circuit calculations",
    badgeIcon: "\u{1F4D0}",
    badgeColor: "blue",
    skills: ["Ohm's Law", "Power calculation", "Circuit analysis"],
    level: "intermediate"
  },
  {
    id: "cert-digital-logic",
    pathId: "digital-logic-intro",
    title: "Digital Logic Pioneer",
    description: "Completed introduction to digital logic circuits",
    badgeIcon: "\u{1F5A5}\uFE0F",
    badgeColor: "purple",
    skills: ["Logic gates", "Truth tables", "Binary logic"],
    level: "intermediate"
  },
  {
    id: "cert-electronics-engineer",
    pathId: "electronics-engineering",
    title: "Junior Electronics Engineer",
    description: "Demonstrated proficiency in advanced circuit design",
    badgeIcon: "\u{1F393}",
    badgeColor: "gold",
    skills: ["Transistors", "Capacitors", "Complex circuits", "Signal processing"],
    level: "advanced"
  }
];
var LEARNING_PATHS = [
  // ==================== Path 1: Circuit Explorers (K-2) ====================
  {
    id: "circuits-explorers",
    title: "Circuit Explorers",
    description: "Discover the exciting world of electricity! Learn how circuits work through fun, hands-on activities.",
    icon: "\u{1F526}",
    color: "green",
    gradeLevel: "K-2",
    keyStage: "KS1",
    estimatedMinutes: 45,
    subjects: ["physics", "technology"],
    standards: ["NGSS-PS3", "UK-KS2-SCI"],
    certificateId: "cert-circuit-explorer",
    modules: [
      {
        id: "ce-module-1",
        pathId: "circuits-explorers",
        order: 1,
        title: "What is Electricity?",
        description: "Learn about electricity and where it comes from",
        difficulty: "beginner",
        estimatedMinutes: 15,
        learningObjectives: [
          "Understand that electricity is a form of energy",
          "Know that electricity needs a path to flow",
          "Recognize batteries as a source of electricity"
        ],
        vocabulary: [
          { term: "Electricity", definition: "A type of energy that powers things", gradeAppropriate: ["K-2", "3-5"] },
          { term: "Battery", definition: "A container that stores electricity", gradeAppropriate: ["K-2", "3-5"] },
          { term: "Light up", definition: "When something glows because electricity is flowing", gradeAppropriate: ["K-2"] }
        ],
        activities: [
          {
            id: "ce-1-explore",
            type: "sandbox",
            title: "Meet the Battery",
            description: "Drag a battery onto the canvas and explore what it does!",
            xpReward: 25,
            requiredForProgress: true
          },
          {
            id: "ce-1-first-circuit",
            type: "tutorial",
            title: "Your First Light",
            description: "Connect a battery to an LED and watch it glow!",
            tutorialLevelId: "basics_1",
            xpReward: 50,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-PS3", "UK-KS2-SCI"]
      },
      {
        id: "ce-module-2",
        pathId: "circuits-explorers",
        order: 2,
        title: "On and Off",
        description: "Use switches to control your circuit like a light switch at home!",
        difficulty: "beginner",
        estimatedMinutes: 15,
        learningObjectives: [
          "Understand that switches control the flow of electricity",
          "Know the difference between open and closed circuits",
          "Be able to add a switch to a circuit"
        ],
        vocabulary: [
          { term: "Switch", definition: "Something that turns electricity on or off", gradeAppropriate: ["K-2", "3-5"] },
          { term: "Open", definition: "When the switch is off and electricity cannot flow", gradeAppropriate: ["K-2", "3-5"] },
          { term: "Closed", definition: "When the switch is on and electricity can flow", gradeAppropriate: ["K-2", "3-5"] }
        ],
        activities: [
          {
            id: "ce-2-switch",
            type: "tutorial",
            title: "Add a Switch",
            description: "Add a switch to control when your LED lights up",
            tutorialLevelId: "basics_2",
            xpReward: 60,
            requiredForProgress: true
          },
          {
            id: "ce-2-challenge",
            type: "challenge",
            title: "Flashlight Challenge",
            description: "Build a circuit that works like a flashlight!",
            xpReward: 40,
            requiredForProgress: false,
            challengeConfig: {
              targetDescription: "Create a circuit with a battery, switch, and LED",
              hints: ["Start with a battery", "Add a switch in the middle", "End with an LED"],
              successCriteria: ["LED lights when switch is closed", "LED is off when switch is open"]
            }
          }
        ],
        standardsAddressed: ["NGSS-PS3", "UK-KS2-SCI"]
      },
      {
        id: "ce-module-3",
        pathId: "circuits-explorers",
        order: 3,
        title: "More Lights!",
        description: "Light up multiple LEDs with one battery",
        difficulty: "beginner",
        estimatedMinutes: 15,
        learningObjectives: [
          "Understand that one battery can power multiple components",
          "Practice connecting multiple LEDs",
          "Explore brightness differences"
        ],
        vocabulary: [
          { term: "LED", definition: "A small light that uses electricity", gradeAppropriate: ["K-2", "3-5"] },
          { term: "Bright", definition: "Giving off a lot of light", gradeAppropriate: ["K-2"] },
          { term: "Dim", definition: "Giving off only a little light", gradeAppropriate: ["K-2"] }
        ],
        activities: [
          {
            id: "ce-3-two-leds",
            type: "tutorial",
            title: "Double the Fun",
            description: "Power two LEDs with one battery!",
            tutorialLevelId: "basics_4",
            xpReward: 75,
            requiredForProgress: true
          }
        ],
        assessment: {
          type: "quiz",
          questions: [
            {
              id: "ce-q1",
              question: "What do you need for electricity to flow?",
              type: "multiple-choice",
              options: ["A complete path (circuit)", "Just a battery", "Only wires", "Nothing special"],
              correctAnswer: "A complete path (circuit)",
              explanation: "Electricity needs a complete path to flow from the battery, through components, and back!",
              points: 10
            },
            {
              id: "ce-q2",
              question: "What does a switch do?",
              type: "multiple-choice",
              options: ["Controls when electricity flows", "Makes more electricity", "Changes colors", "Nothing"],
              correctAnswer: "Controls when electricity flows",
              explanation: "A switch opens and closes the circuit to control when electricity can flow!",
              points: 10
            }
          ],
          passingScore: 70
        },
        standardsAddressed: ["NGSS-PS3", "UK-KS2-SCI"]
      }
    ]
  },
  // ==================== Path 2: Electricity Basics (3-5) ====================
  {
    id: "electricity-basics",
    title: "Electricity Basics",
    description: "Build on your knowledge! Learn how electricity flows, what makes circuits complete, and design your own working circuits.",
    icon: "\u{1F4A1}",
    color: "yellow",
    gradeLevel: "3-5",
    keyStage: "KS2",
    estimatedMinutes: 90,
    subjects: ["physics", "engineering"],
    standards: ["NGSS-PS2", "NGSS-PS3", "UK-KS2-SCI"],
    prerequisites: ["circuits-explorers"],
    certificateId: "cert-electricity-fundamentals",
    modules: [
      {
        id: "eb-module-1",
        pathId: "electricity-basics",
        order: 1,
        title: "Complete Circuits",
        description: "Understand why circuits must be complete for electricity to flow",
        difficulty: "beginner",
        estimatedMinutes: 20,
        learningObjectives: [
          "Explain why a circuit must be complete",
          "Identify breaks in a circuit",
          "Use ground connections properly"
        ],
        vocabulary: [
          { term: "Complete circuit", definition: "A path with no breaks where electricity can flow all the way around", gradeAppropriate: ["3-5", "6-8"] },
          { term: "Ground", definition: "The return path for electricity, like the negative side of a battery", gradeAppropriate: ["3-5", "6-8"] },
          { term: "Current", definition: "The flow of electricity through a wire", gradeAppropriate: ["3-5", "6-8"] }
        ],
        activities: [
          {
            id: "eb-1-ground",
            type: "tutorial",
            title: "Understanding Ground",
            description: "Learn about ground and why circuits need a return path",
            tutorialLevelId: "basics_3",
            xpReward: 50,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-PS3", "UK-KS2-SCI"]
      },
      {
        id: "eb-module-2",
        pathId: "electricity-basics",
        order: 2,
        title: "Circuit Safety",
        description: "Learn about fuses and how to protect circuits",
        difficulty: "beginner",
        estimatedMinutes: 20,
        learningObjectives: [
          "Understand why circuits need protection",
          "Know what a fuse does",
          "Design a protected circuit"
        ],
        vocabulary: [
          { term: "Fuse", definition: "A safety device that breaks the circuit if too much current flows", gradeAppropriate: ["3-5", "6-8"] },
          { term: "Overcurrent", definition: "When too much electricity flows through a circuit", gradeAppropriate: ["3-5", "6-8"] },
          { term: "Short circuit", definition: "A dangerous path that lets too much current flow", gradeAppropriate: ["3-5", "6-8"] }
        ],
        activities: [
          {
            id: "eb-2-fuse",
            type: "tutorial",
            title: "Circuit Protection",
            description: "Add a fuse to protect your circuit from damage",
            tutorialLevelId: "basics_5",
            xpReward: 80,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-ETS1", "UK-KS2-SCI"]
      },
      {
        id: "eb-module-3",
        pathId: "electricity-basics",
        order: 3,
        title: "LED Circuit Design",
        description: "Design circuits with LEDs and understand why resistors are important",
        difficulty: "intermediate",
        estimatedMinutes: 25,
        learningObjectives: [
          "Understand why LEDs need resistors",
          "Know what happens without a resistor (LED burns out)",
          "Calculate simple resistor values"
        ],
        vocabulary: [
          { term: "Resistor", definition: "A component that limits how much current can flow", gradeAppropriate: ["3-5", "6-8"] },
          { term: "LED", definition: "Light Emitting Diode - a type of light that only works one direction", gradeAppropriate: ["3-5", "6-8"] },
          { term: "Brightness", definition: "How much light an LED gives off, controlled by current", gradeAppropriate: ["3-5", "6-8"] }
        ],
        activities: [
          {
            id: "eb-3-led-basics",
            type: "tutorial",
            title: "LED Fundamentals",
            description: "Learn why LEDs need resistors",
            tutorialLevelId: "led_circuits_1",
            xpReward: 75,
            requiredForProgress: true
          },
          {
            id: "eb-3-brightness",
            type: "tutorial",
            title: "Brightness Control",
            description: "Control LED brightness with different resistors",
            tutorialLevelId: "led_circuits_2",
            xpReward: 80,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-PS3", "NGSS-ETS1"]
      },
      {
        id: "eb-module-4",
        pathId: "electricity-basics",
        order: 4,
        title: "Series Circuits",
        description: "Connect components in a row to create series circuits",
        difficulty: "intermediate",
        estimatedMinutes: 25,
        learningObjectives: [
          "Understand series circuit configuration",
          "Know that current is the same through all components",
          "Build a working series LED circuit"
        ],
        vocabulary: [
          { term: "Series circuit", definition: "Components connected end-to-end in a single path", gradeAppropriate: ["3-5", "6-8"] },
          { term: "Same current", definition: "In series, electricity flows through each part equally", gradeAppropriate: ["3-5", "6-8"] }
        ],
        activities: [
          {
            id: "eb-4-series-leds",
            type: "tutorial",
            title: "LEDs in Series",
            description: "Connect multiple LEDs in series",
            tutorialLevelId: "led_circuits_3",
            xpReward: 90,
            requiredForProgress: true
          }
        ],
        assessment: {
          type: "quiz",
          questions: [
            {
              id: "eb-q1",
              question: "Why do LEDs need resistors?",
              type: "multiple-choice",
              options: ["To limit current and prevent burning out", "To make them brighter", "To change their color", "They don't need them"],
              correctAnswer: "To limit current and prevent burning out",
              explanation: "Without a resistor, too much current flows through the LED and it burns out!",
              points: 15
            },
            {
              id: "eb-q2",
              question: "In a series circuit, the current through each component is:",
              type: "multiple-choice",
              options: ["The same", "Different for each", "Zero", "Infinite"],
              correctAnswer: "The same",
              explanation: "In series, there's only one path, so the same current flows through everything!",
              points: 15
            },
            {
              id: "eb-q3",
              question: "What does a fuse do when too much current flows?",
              type: "multiple-choice",
              options: ["It breaks to stop the current", "It makes more current", "It stores electricity", "Nothing"],
              correctAnswer: "It breaks to stop the current",
              explanation: "A fuse is a safety device that breaks (blows) to protect the circuit!",
              points: 10
            }
          ],
          passingScore: 70
        },
        standardsAddressed: ["NGSS-PS2", "NGSS-PS3"]
      }
    ]
  },
  // ==================== Path 3: Ohm's Law Mastery (6-8) ====================
  {
    id: "ohms-law-mastery",
    title: "Ohm's Law Mastery",
    description: "Master the fundamental law of electricity! Learn to calculate voltage, current, and resistance using V = I \xD7 R.",
    icon: "\u{1F4CA}",
    color: "blue",
    gradeLevel: "6-8",
    keyStage: "KS3",
    estimatedMinutes: 120,
    subjects: ["physics", "mathematics"],
    standards: ["NGSS-MS-PS2", "UK-KS3-PHY", "COMMON-CORE-MATH"],
    prerequisites: ["electricity-basics"],
    certificateId: "cert-ohms-law-master",
    modules: [
      {
        id: "olm-module-1",
        pathId: "ohms-law-mastery",
        order: 1,
        title: "The Magic Formula",
        description: "Discover V = I \xD7 R, the most important formula in electronics",
        difficulty: "intermediate",
        estimatedMinutes: 25,
        learningObjectives: [
          "State Ohm's Law (V = I \xD7 R)",
          "Define voltage, current, and resistance",
          "Use the formula to calculate unknown values"
        ],
        vocabulary: [
          { term: "Voltage (V)", definition: "Electrical pressure that pushes current, measured in Volts", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Current (I)", definition: "The rate of electron flow, measured in Amperes (A)", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Resistance (R)", definition: "Opposition to current flow, measured in Ohms (\u03A9)", gradeAppropriate: ["6-8", "9-12"] }
        ],
        activities: [
          {
            id: "olm-1-intro",
            type: "tutorial",
            title: "Ohm's Law Introduction",
            description: "Learn the relationship between voltage, current, and resistance",
            tutorialLevelId: "ohms_law_1",
            xpReward: 75,
            requiredForProgress: true
          },
          {
            id: "olm-1-resistance",
            type: "tutorial",
            title: "Changing Resistance",
            description: "See how resistance affects current flow",
            tutorialLevelId: "ohms_law_2",
            xpReward: 80,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-MS-PS2", "UK-KS3-PHY"]
      },
      {
        id: "olm-module-2",
        pathId: "ohms-law-mastery",
        order: 2,
        title: "Voltage Division",
        description: "Understand how voltage divides across components",
        difficulty: "intermediate",
        estimatedMinutes: 25,
        learningObjectives: [
          "Explain voltage division in series circuits",
          "Calculate voltage drops across resistors",
          "Apply Kirchhoff's Voltage Law"
        ],
        vocabulary: [
          { term: "Voltage drop", definition: "The decrease in voltage across a component", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Kirchhoff's Voltage Law", definition: "The sum of voltages around a closed loop equals zero", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Voltage divider", definition: "Two resistors that create a fraction of input voltage", gradeAppropriate: ["6-8", "9-12"] }
        ],
        activities: [
          {
            id: "olm-2-voltage-div",
            type: "tutorial",
            title: "Voltage Division",
            description: "See how voltage divides across resistors",
            tutorialLevelId: "ohms_law_3",
            xpReward: 85,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-MS-PS2", "COMMON-CORE-MATH"]
      },
      {
        id: "olm-module-3",
        pathId: "ohms-law-mastery",
        order: 3,
        title: "Power Calculation",
        description: "Calculate electrical power using P = V \xD7 I",
        difficulty: "intermediate",
        estimatedMinutes: 25,
        learningObjectives: [
          "Calculate power using P = V \xD7 I",
          "Understand power dissipation in resistors",
          "Relate power to energy consumption"
        ],
        vocabulary: [
          { term: "Power (P)", definition: "Rate of energy use, measured in Watts (W)", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Watt", definition: "Unit of power, equal to one Joule per second", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Dissipation", definition: "Energy released as heat in a resistor", gradeAppropriate: ["6-8", "9-12"] }
        ],
        activities: [
          {
            id: "olm-3-power",
            type: "tutorial",
            title: "Power Calculation",
            description: "Calculate power using P = V \xD7 I",
            tutorialLevelId: "ohms_law_4",
            xpReward: 90,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-MS-PS2", "COMMON-CORE-MATH"]
      },
      {
        id: "olm-module-4",
        pathId: "ohms-law-mastery",
        order: 4,
        title: "Series & Parallel Analysis",
        description: "Analyze complex series and parallel circuits",
        difficulty: "advanced",
        estimatedMinutes: 45,
        learningObjectives: [
          "Calculate equivalent resistance in series and parallel",
          "Analyze current division in parallel circuits",
          "Design circuits to meet specifications"
        ],
        vocabulary: [
          { term: "Equivalent resistance", definition: "A single resistance value that replaces a combination", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Parallel resistance", definition: "1/R_total = 1/R1 + 1/R2 + ...", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Current division", definition: "Total current splits among parallel branches", gradeAppropriate: ["6-8", "9-12"] }
        ],
        activities: [
          {
            id: "olm-4-series",
            type: "tutorial",
            title: "Resistors in Series",
            description: "Calculate total resistance in series",
            tutorialLevelId: "series_parallel_1",
            xpReward: 75,
            requiredForProgress: true
          },
          {
            id: "olm-4-parallel",
            type: "tutorial",
            title: "Resistors in Parallel",
            description: "Calculate equivalent parallel resistance",
            tutorialLevelId: "series_parallel_2",
            xpReward: 85,
            requiredForProgress: true
          },
          {
            id: "olm-4-divider",
            type: "tutorial",
            title: "Voltage Divider Circuit",
            description: "Build a practical voltage divider",
            tutorialLevelId: "series_parallel_3",
            xpReward: 90,
            requiredForProgress: true
          },
          {
            id: "olm-4-challenge",
            type: "tutorial",
            title: "Ohm's Law Challenge",
            description: "Design a circuit for specific current",
            tutorialLevelId: "ohms_law_5",
            xpReward: 100,
            requiredForProgress: true
          }
        ],
        assessment: {
          type: "quiz",
          questions: [
            {
              id: "olm-q1",
              question: "If V = 12V and R = 4\u03A9, what is the current (I)?",
              type: "multiple-choice",
              options: ["3A", "48A", "0.33A", "16A"],
              correctAnswer: "3A",
              explanation: "Using I = V/R: I = 12V / 4\u03A9 = 3A",
              points: 20
            },
            {
              id: "olm-q2",
              question: "Two 100\u03A9 resistors in parallel have an equivalent resistance of:",
              type: "multiple-choice",
              options: ["50\u03A9", "200\u03A9", "100\u03A9", "25\u03A9"],
              correctAnswer: "50\u03A9",
              explanation: "For equal resistors in parallel: R_eq = R/n = 100\u03A9/2 = 50\u03A9",
              points: 20
            },
            {
              id: "olm-q3",
              question: "If a circuit uses 2A at 9V, how much power does it use?",
              type: "multiple-choice",
              options: ["18W", "4.5W", "11W", "7W"],
              correctAnswer: "18W",
              explanation: "P = V \xD7 I = 9V \xD7 2A = 18W",
              points: 20
            }
          ],
          passingScore: 70
        },
        standardsAddressed: ["NGSS-MS-PS2", "UK-KS3-PHY", "COMMON-CORE-MATH"]
      }
    ]
  },
  // ==================== Path 4: Digital Logic Introduction (6-8 / 9-12) ====================
  {
    id: "digital-logic-intro",
    title: "Digital Logic Introduction",
    description: "Enter the digital world! Learn about AND, OR, and NOT gates - the building blocks of computers.",
    icon: "\u{1F5A5}\uFE0F",
    color: "purple",
    gradeLevel: "6-8",
    keyStage: "KS3",
    estimatedMinutes: 90,
    subjects: ["technology", "mathematics"],
    standards: ["NGSS-ETS1", "UK-KS3-PHY"],
    prerequisites: ["ohms-law-mastery"],
    certificateId: "cert-digital-logic",
    modules: [
      {
        id: "dli-module-1",
        pathId: "digital-logic-intro",
        order: 1,
        title: "Binary and Logic",
        description: "Understand binary (1s and 0s) and basic logic concepts",
        difficulty: "intermediate",
        estimatedMinutes: 20,
        learningObjectives: [
          "Explain binary representation (1 = HIGH, 0 = LOW)",
          "Understand Boolean logic basics",
          "Recognize logic gates symbols"
        ],
        vocabulary: [
          { term: "Binary", definition: "A number system using only 0 and 1", gradeAppropriate: ["6-8", "9-12"] },
          { term: "HIGH/LOW", definition: "The two states: HIGH = 1 = ON, LOW = 0 = OFF", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Logic gate", definition: "A component that performs a Boolean operation", gradeAppropriate: ["6-8", "9-12"] }
        ],
        activities: [
          {
            id: "dli-1-and",
            type: "tutorial",
            title: "The AND Gate",
            description: "Discover how AND gates work",
            tutorialLevelId: "logic_gates_1",
            xpReward: 80,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-ETS1"]
      },
      {
        id: "dli-module-2",
        pathId: "digital-logic-intro",
        order: 2,
        title: "OR and NOT Gates",
        description: "Learn about OR and NOT (inverter) gates",
        difficulty: "intermediate",
        estimatedMinutes: 25,
        learningObjectives: [
          "Build and test OR gate circuits",
          "Understand NOT gate (inversion)",
          "Create truth tables for each gate"
        ],
        vocabulary: [
          { term: "OR gate", definition: "Output is 1 if ANY input is 1", gradeAppropriate: ["6-8", "9-12"] },
          { term: "NOT gate", definition: "Output is the opposite of the input (inverter)", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Truth table", definition: "A table showing all input/output combinations", gradeAppropriate: ["6-8", "9-12"] }
        ],
        activities: [
          {
            id: "dli-2-or",
            type: "tutorial",
            title: "The OR Gate",
            description: "Learn how OR gates process inputs",
            tutorialLevelId: "logic_gates_2",
            xpReward: 80,
            requiredForProgress: true
          },
          {
            id: "dli-2-not",
            type: "tutorial",
            title: "The NOT Gate",
            description: "Explore signal inversion",
            tutorialLevelId: "logic_gates_3",
            xpReward: 75,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-ETS1", "UK-KS3-PHY"]
      },
      {
        id: "dli-module-3",
        pathId: "digital-logic-intro",
        order: 3,
        title: "Combined Logic",
        description: "Combine gates to create complex logic functions",
        difficulty: "advanced",
        estimatedMinutes: 45,
        learningObjectives: [
          "Build NAND gates from AND and NOT",
          "Understand XOR logic",
          "Design logic circuits from requirements"
        ],
        vocabulary: [
          { term: "NAND gate", definition: "NOT-AND: output is LOW only when all inputs are HIGH", gradeAppropriate: ["6-8", "9-12"] },
          { term: "XOR gate", definition: "Exclusive OR: output is HIGH when inputs differ", gradeAppropriate: ["6-8", "9-12"] },
          { term: "Universal gate", definition: "NAND and NOR can build any logic function", gradeAppropriate: ["9-12"] }
        ],
        activities: [
          {
            id: "dli-3-nand",
            type: "tutorial",
            title: "Build a NAND Gate",
            description: "Combine AND and NOT gates",
            tutorialLevelId: "logic_gates_4",
            xpReward: 90,
            requiredForProgress: true
          },
          {
            id: "dli-3-xor",
            type: "tutorial",
            title: "XOR Challenge",
            description: "Build the exclusive OR function",
            tutorialLevelId: "logic_gates_5",
            xpReward: 120,
            requiredForProgress: true
          }
        ],
        assessment: {
          type: "quiz",
          questions: [
            {
              id: "dli-q1",
              question: "An AND gate with inputs A=1, B=0 outputs:",
              type: "multiple-choice",
              options: ["0", "1", "Depends on voltage", "Error"],
              correctAnswer: "0",
              explanation: "AND requires ALL inputs to be 1. With B=0, output is 0.",
              points: 15
            },
            {
              id: "dli-q2",
              question: "An OR gate with inputs A=0, B=1 outputs:",
              type: "multiple-choice",
              options: ["1", "0", "Depends on voltage", "Error"],
              correctAnswer: "1",
              explanation: "OR outputs 1 if ANY input is 1. With B=1, output is 1.",
              points: 15
            },
            {
              id: "dli-q3",
              question: "What does a NOT gate do?",
              type: "multiple-choice",
              options: ["Inverts the input", "Adds inputs", "Multiplies inputs", "Nothing"],
              correctAnswer: "Inverts the input",
              explanation: "NOT gate outputs the opposite: 0 becomes 1, 1 becomes 0.",
              points: 10
            }
          ],
          passingScore: 70
        },
        standardsAddressed: ["NGSS-ETS1", "UK-KS3-PHY"]
      }
    ]
  },
  // ==================== Path 5: Electronics Engineering (9-12) ====================
  {
    id: "electronics-engineering",
    title: "Electronics Engineering",
    description: "Advanced circuit design with transistors, capacitors, and complex systems. Prepare for real engineering!",
    icon: "\u2699\uFE0F",
    color: "red",
    gradeLevel: "9-12",
    keyStage: "KS4",
    estimatedMinutes: 180,
    subjects: ["engineering", "physics", "technology"],
    standards: ["NGSS-HS-PS2", "NGSS-ETS1", "UK-KS4-PHY"],
    prerequisites: ["digital-logic-intro"],
    certificateId: "cert-electronics-engineer",
    modules: [
      {
        id: "ee-module-1",
        pathId: "electronics-engineering",
        order: 1,
        title: "Transistor Fundamentals",
        description: "Master the transistor - the heart of modern electronics",
        difficulty: "advanced",
        estimatedMinutes: 45,
        learningObjectives: [
          "Explain transistor operation (NPN/PNP)",
          "Use transistors as switches",
          "Calculate base resistor values"
        ],
        vocabulary: [
          { term: "Transistor", definition: "A semiconductor that amplifies or switches signals", gradeAppropriate: ["9-12"] },
          { term: "Base", definition: "Control terminal that turns the transistor on/off", gradeAppropriate: ["9-12"] },
          { term: "Collector/Emitter", definition: "Main current path controlled by the base", gradeAppropriate: ["9-12"] },
          { term: "Gain (\u03B2)", definition: "Amplification factor: I_collector = \u03B2 \xD7 I_base", gradeAppropriate: ["9-12"] }
        ],
        activities: [
          {
            id: "ee-1-transistor",
            type: "tutorial",
            title: "Transistor Basics",
            description: "Use a transistor to switch an LED",
            tutorialLevelId: "advanced_1",
            xpReward: 100,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-HS-PS2", "UK-KS4-PHY"]
      },
      {
        id: "ee-module-2",
        pathId: "electronics-engineering",
        order: 2,
        title: "Capacitors and Timing",
        description: "Learn about energy storage and RC timing circuits",
        difficulty: "advanced",
        estimatedMinutes: 45,
        learningObjectives: [
          "Understand capacitor charging/discharging",
          "Calculate time constants (\u03C4 = R \xD7 C)",
          "Design RC timing circuits"
        ],
        vocabulary: [
          { term: "Capacitor", definition: "Stores energy in an electric field", gradeAppropriate: ["9-12"] },
          { term: "Capacitance", definition: "Ability to store charge, measured in Farads", gradeAppropriate: ["9-12"] },
          { term: "Time constant (\u03C4)", definition: "\u03C4 = R \xD7 C, time to reach 63% charge", gradeAppropriate: ["9-12"] },
          { term: "RC circuit", definition: "Resistor-capacitor combination for timing", gradeAppropriate: ["9-12"] }
        ],
        activities: [
          {
            id: "ee-2-capacitor",
            type: "tutorial",
            title: "Capacitor Charging",
            description: "Build an RC circuit and observe charging",
            tutorialLevelId: "advanced_2",
            xpReward: 100,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-HS-PS2", "UK-KS4-PHY"]
      },
      {
        id: "ee-module-3",
        pathId: "electronics-engineering",
        order: 3,
        title: "Oscillators and Timing",
        description: "Create circuits that oscillate automatically",
        difficulty: "expert",
        estimatedMinutes: 45,
        learningObjectives: [
          "Build an astable oscillator",
          "Control oscillation frequency",
          "Understand feedback in oscillators"
        ],
        vocabulary: [
          { term: "Oscillator", definition: "Circuit that switches states repeatedly", gradeAppropriate: ["9-12"] },
          { term: "Astable", definition: "Has no stable state - always changing", gradeAppropriate: ["9-12"] },
          { term: "Frequency", definition: "How fast the oscillator switches", gradeAppropriate: ["9-12"] }
        ],
        activities: [
          {
            id: "ee-3-blinker",
            type: "tutorial",
            title: "LED Blinker",
            description: "Build an automatic blinking circuit",
            tutorialLevelId: "advanced_3",
            xpReward: 120,
            requiredForProgress: true
          }
        ],
        standardsAddressed: ["NGSS-HS-PS2", "NGSS-ETS1"]
      },
      {
        id: "ee-module-4",
        pathId: "electronics-engineering",
        order: 4,
        title: "Amplifier Design",
        description: "Design a transistor amplifier circuit",
        difficulty: "expert",
        estimatedMinutes: 45,
        learningObjectives: [
          "Design a common-emitter amplifier",
          "Calculate bias resistors",
          "Understand voltage gain"
        ],
        vocabulary: [
          { term: "Amplifier", definition: "Circuit that increases signal strength", gradeAppropriate: ["9-12"] },
          { term: "Bias", definition: "DC voltage that sets the operating point", gradeAppropriate: ["9-12"] },
          { term: "Gain", definition: "Ratio of output to input signal", gradeAppropriate: ["9-12"] }
        ],
        activities: [
          {
            id: "ee-4-amp",
            type: "tutorial",
            title: "Transistor Amplifier",
            description: "Build a signal amplifier",
            tutorialLevelId: "advanced_4",
            xpReward: 130,
            requiredForProgress: true
          },
          {
            id: "ee-4-sensor",
            type: "tutorial",
            title: "Final Project",
            description: "Build a light-controlled circuit",
            tutorialLevelId: "advanced_5",
            xpReward: 150,
            requiredForProgress: true
          }
        ],
        assessment: {
          type: "project",
          projectPrompt: "Design and build a circuit that automatically turns on an LED when it gets dark. Document your design choices and calculations.",
          passingScore: 70
        },
        standardsAddressed: ["NGSS-HS-PS2", "NGSS-ETS1", "UK-KS4-PHY"]
      }
    ]
  }
];
function getLearningPathById(pathId) {
  return LEARNING_PATHS.find((p) => p.id === pathId);
}
function getLearningPathsForGrade(grade) {
  return LEARNING_PATHS.filter((p) => p.gradeLevel === grade);
}
function getCertificateById(certId) {
  return CERTIFICATES.find((c) => c.id === certId);
}
function getCertificateForPath(pathId) {
  return CERTIFICATES.find((c) => c.pathId === pathId);
}
function getAllSkills() {
  return SKILLS;
}
function getSkillsForGrade(grade) {
  return SKILLS.filter((s) => s.gradeLevel === grade);
}
function calculatePathTotalXP(pathId) {
  const path = getLearningPathById(pathId);
  if (!path) return 0;
  return path.modules.reduce((total, module) => {
    const moduleXP = module.activities.reduce((sum, act) => sum + act.xpReward, 0);
    return total + moduleXP;
  }, 0);
}
function getAllModules() {
  return LEARNING_PATHS.flatMap(
    (path) => path.modules.map((module) => ({
      ...module,
      pathTitle: path.title
    }))
  );
}
export {
  ACHIEVEMENT_REQUIREMENTS,
  CANVAS_DEFAULT_HEIGHT,
  CANVAS_DEFAULT_WIDTH,
  CERTIFICATES,
  COLORS,
  COMPONENT_CATEGORIES,
  COMPONENT_DEFAULTS,
  COMPONENT_EDUCATION,
  DEFAULT_SIMULATION_OPTIONS,
  DEFAULT_USER_SETTINGS,
  GRADE_LEVEL_INFO,
  GRID_SIZE,
  LEARNING_PATHS,
  RATING_THRESHOLDS,
  SIMULATION,
  SKILLS,
  TUTORIAL_CHAPTERS,
  calculatePathTotalXP,
  clamp,
  debounce,
  deepClone,
  degToRad,
  distance,
  formatSI,
  formatTime,
  generateId,
  getAllLevels,
  getAllModules,
  getAllSkills,
  getCertificateById,
  getCertificateForPath,
  getChapterById,
  getLearningPathById,
  getLearningPathsForGrade,
  getLevelById,
  getLevelsForChapter,
  getSkillsForGrade,
  getTotalLevelCount,
  radToDeg,
  snapToGrid,
  throttle
};
